/**
 * Dicionário de textos em espanhol.
 *
 * Mesma árvore de `pt-BR.ts`, galho por galho: é o `satisfies Dicionario` do fim
 * do arquivo que cobra isso. Chave a mais, chave a menos ou texto onde deveria
 * haver galho vira erro de compilação aqui, e não texto faltando na tela de
 * quem está jogando.
 *
 * O espanhol é neutro com vocabulário de futebol da Espanha, que é o que
 * circula nos jogos do gênero e o que a América Latina reconhece sem tropeço:
 * `plantilla`, `alineación`, `jornada`, `goleadores`, `descenso`, `clásico`,
 * `fichajes`. Onde a palavra muda de país, vale a que se entende nos dois lados
 * do Atlântico.
 *
 * Português e espanhol são parecidos, e é justamente por isso que a tradução
 * precisa de cuidado: `elenco` não é `elenco`, é `plantilla`, e `rodada` não é
 * `ronda`, é `jornada`. As poucas chaves que ficam iguais ao português ficam
 * porque a palavra é mesmo a mesma, e o teste em `idiomas.test.ts` guarda a
 * lista delas uma a uma.
 */
import type { Dicionario } from "./nucleo";

export const es = {
  // -------------------------------------------------------------------------
  // Comum a várias telas
  // -------------------------------------------------------------------------
  tabela: {
    buscar: "Buscar",
    ordenarPor: "Ordenar por {coluna}",
    nadaEncontrado: "No se encontró nada.",
  },

  comum: {
    marcaLinha1: "LEGACY",
    marcaLinha2: "FOOTBALL MANAGER",
    /** O nome do produto inteiro, para o emblema de quem usa leitor de tela. */
    marca: "Legacy Football Manager",

    fechar: "Cerrar",
    cancelar: "Cancelar",
    continuar: "Continuar",
    voltar: "Volver",
    naFila: "en cola",
    vazio: "-",
    semClube: "Sin club",
    clubeDesconhecido: "otro club",
    condicao: "Condición",
    estrelas: "{marcadas} de {total} estrellas",
    moral: "Moral {valor}",
    semJogos: "Sin partidos",
    pontos: "puntos",

    caracteristicas: {
      arm: "Creación",
      cab: "Juego aéreo",
      cru: "Centros",
      des: "Entradas",
      dri: "Regate",
      fin: "Definición",
      mar: "Marcaje",
      pas: "Pase",
      res: "Resistencia",
      vel: "Velocidad",
      col: "Colocación",
      sai: "Salida",
      ref: "Reflejos",
      pen: "Penaltis",
    },

    setores: {
      GOL: "Porteros",
      DEF: "Defensas",
      MEI: "Centrocampistas",
      ATA: "Delanteros",
    },

    /*
     * O espanhol abrevia mil millones como `mil M`, e não com uma letra só: `B`
     * é billón em inglês e outra coisa em espanhol, e trocar isso numa tela de
     * dinheiro seria erro de três casas.
     */
    dinheiro: {
      negativo: "-",
      bilhoes: "{sinal}$ {valor} mil M",
      milhoes: "{sinal}$ {valor} M",
      milhares: "{sinal}$ {valor} mil",
      unidade: "{sinal}$ {valor}",
    },

    data: {
      rodada: "{dia} {mes} {ano} · {diaDaSemana}",
      diasDaSemana: {
        domingo: "Domingo",
        segunda: "Lunes",
        terca: "Martes",
        quarta: "Miércoles",
        quinta: "Jueves",
        sexta: "Viernes",
        sabado: "Sábado",
      },
      meses: {
        jan: "Ene",
        fev: "Feb",
        mar: "Mar",
        abr: "Abr",
        mai: "May",
        jun: "Jun",
        jul: "Jul",
        ago: "Ago",
        set: "Sep",
        out: "Oct",
        nov: "Nov",
        dez: "Dic",
      },
    },

    tempo: {
      agora: "ahora",
      minutos: "hace {valor} min",
      horas: "hace {valor} h",
      dias: "hace {valor} día(s)",

      acabou: "plazo vencido",
      contagemDias: "{dias}d {horas}h",
      contagemHoras: "{horas}h {minutos}min",
      contagemMinutos: "{minutos}min {segundos}s",
    },
  },

  // -------------------------------------------------------------------------
  // Modo online, o que vale em qualquer tela dele
  // -------------------------------------------------------------------------
  online: {
    carregando: "Cargando...",
    /*
     * O servidor manda a mensagem de erro pronta, e hoje ela sai em português
     * qualquer que seja o idioma do jogo. Este texto cobre só o que não veio da
     * API, como uma falha dentro do próprio navegador.
     */
    erroInesperado: "Algo salió mal. Inténtalo de nuevo.",
    tentarDeNovo: "Intentar de nuevo",
    conectado: "conectado",
    reconectando: "reconectando...",
    precisaAtualizar:
      "El servidor habla una versión distinta del protocolo. Actualiza el juego para volver al online.",
  },

  // -------------------------------------------------------------------------
  // Sessão de jogo solo
  // -------------------------------------------------------------------------
  sessao: {
    semJogo: "No hay ninguna partida en curso.",
    semClubeEscolhido: "No elegiste ningún club.",
    semClubeValido: "Ningún club válido para dirigir.",
    treinadorForaDaPartida: "Ese entrenador no está en esta partida.",
    saveNaoEncontrado: "No se encontró la partida guardada.",
    semArmazenamento: "Este navegador no deja guardar partidas.",
    armazenamentoRecusou: "El navegador rechazó el almacenamiento de la partida.",
    naoSalvou: "No se pudo guardar. El juego sigue, pero el avance no queda guardado.",
    encerrada: "La sesión de juego terminó. Recarga la página.",
    motorParou: "El motor dejó de responder.",
    falhaInesperada: "Algo salió mal en el motor: {detalhe}",
    arquivoNaoServe: "Este archivo no sirve. {problemas}",

    temporadaEncerrada: "Temporada terminada. Avanza a la pretemporada.",
    rodadaSemJogo: "Jornada {rodada} jugada. Tu equipo no tuvo partido esta jornada.",

    semPartidaAoVivo: "No hay ningún partido en curso.",
    semPartidaNaRodada: "No hay partido en vivo en esta jornada.",
    trocaImpossivel: "Ese cambio no es posible ahora.",

    janelaFechada: "El mercado de pases está cerrado.",
    jogadorJaEhSeu: "Este jugador ya es tuyo.",
    elencoNoLimite: "El plantel ya llegó al límite de {maximo} jugadores.",
    naoDesceDePatamar: "{jogador} no aceptaría bajar tanto de nivel.",
    salarioAbaixoDoPedido: "{jogador} pide al menos {salario} por jornada.",
    caixaNaoCobreEntrada:
      "La caja no cubre la entrada de esta oferta sumada a las que ya están en cola.",
    propostaMuitoBaixa: "Una oferta tan baja sería rechazada al instante.",
    compraIndisponivel: "No se puede comprar a {jogador} en el acto.",
    caixaNaoCobreCompra: "La caja no cubre la compra sumada a las ofertas que ya están en cola.",
    propostaFechada: "Esta oferta ya no está abierta.",
    propostaDeOutroClube: "Esta oferta no es tuya para responder.",
    coletivaFechada: "Esa rueda de prensa ya terminó.",

    olheiroNoProprioElenco: "No necesitas un ojeador para tu propio plantel.",
    caixaNaoCobreOlheiro: "La caja no cubre el costo del ojeador sumado a lo que ya está en cola.",
    olheiroJaNaFila: "Ese ojeador ya está en cola.",

    jogadorDeOutroClube: "Este jugador no es tuyo.",
    acaoDesconhecida: "Acción desconocida.",

    garotoForaDaBase: "Ese chico no está en tus inferiores.",
    garotoNoLimiteDeIdade: "{jogador} no puede pasar de los {idade} años en inferiores.",

    ingressoForaDaFaixa: "La entrada tiene que quedar entre {minimo} y {maximo}.",
    bancoLiberaAte: "El banco presta {valor} como máximo.",
    caixaNaoCobrePagamento: "La caja no cubre ese pago.",
    obraEmAndamento: "Termina la obra actual antes de empezar otra.",
    estruturaDesconhecida: "Instalación desconocida.",
    obraUmNivelPorVez: "Las instalaciones suben un nivel por vez.",
    caixaNaoCobreObra: "La caja no cubre la obra sumada a lo que ya está en cola.",

    fila: {
      escalacaoDefensiva: "Alineación en {formacao}, juego defensivo",
      escalacaoEquilibrada: "Alineación en {formacao}, juego equilibrado",
      escalacaoOfensiva: "Alineación en {formacao}, juego ofensivo",
      proposta: "Oferta por {jogador}",
      aceitarProposta: "Aceptar la oferta por {jogador}",
      recusarProposta: "Rechazar la oferta por {jogador}",
      elogiar: "Elogiar a {jogador}",
      criticar: "Llamar la atención a {jogador}",
      renovar: "Renovar el contrato de {jogador}",
      dispensar: "Rescindir con {jogador}",
      listar: "Poner a {jogador} en la lista de transferibles",
      tirarDaLista: "Sacar a {jogador} de la lista",
      observar: "Observar a {jogador}",
      emprestimo: "Pedir a {jogador} a préstamo, cubriendo el {parte}% del sueldo",
      promover: "Subir al plantel: {jogador}",
      manter: "Retener un año más en inferiores: {jogador}",
      dispensarDaBase: "Dejar libre de inferiores: {jogador}",
      venderDaBase: "Vender del equipo B: {jogador}",
      trocarTecnicoDaBase: "Cambiar el técnico del equipo B",
      ingresso: "Entrada a {valor}",
      emprestimoNoBanco: "Pedir un préstamo al banco",
      abaterDivida: "Bajar la deuda",
      obra: "Empezar una obra",
      clube: "Ajuste en el club",
      jogadorDesconhecido: "el jugador",
      garotoDesconhecido: "el chico de inferiores",
      coletiva: "Responder en la rueda de prensa",
      coletivaSemComentarios: "Rueda de prensa: sin comentarios",
    },
  },

  // -------------------------------------------------------------------------
  // Início
  // -------------------------------------------------------------------------
  home: {
    lema: "Escribe tu historia. Deja tu legado.",
    continuarCom: "Continuar con el {clube}",
    novaCarreira: "Nueva carrera",
    bancoProprio: "Empezar con una base de datos propia",
    editorDeBanco: "Editor de base de datos",
    erroDeArquivo: "No pude leer el archivo. Tiene que ser un JSON válido.",
    carreirasSalvas: "Cargar partida",
    importarSave: "Importar archivo .lmsave",
    exportarSave: "Exportar",
    saveImportado: "Carrera importada",
    erroDeSave: "Ese archivo no es una carrera .lmsave.",
    resumoDoSave: "{treinador} · temporada {temporada}, jornada {rodada}/{total} · {quando}",
    licenca: {
      teste: "Prueba gratis · {dias} días",
      ultimoDia: "Prueba gratis · último día",
      encerrado: "Prueba terminada",
      testeUsado: "Prueba ya usada en este dispositivo",
      relogio: "Revisa la fecha y la hora del dispositivo",
      comprar: "Comprar el juego",
    },
    apagar: "Borrar",
    modo: {
      umJogador: "1 Jugador",
      online: "Multijugador en línea",
      trocar: "Cambiar de modo",
    },

    online: {
      procurando: "Buscando tu carrera.",
      comecar: "Empezar carrera",
      continuar: "Continuar con {clube}",
      resumo: "{treinador} · temporada {temporada}, jornada {rodada} · {quando}",
      exportar: "Exportar para 1 Jugador",
      apagar: "Borrar carrera",
      apagarConfirma: "¿Borrar la carrera de esta cuenta? No tiene vuelta.",
    },


    versao: "alfa · versión {versao}",
  },

  // -------------------------------------------------------------------------
  // Nova carreira
  // -------------------------------------------------------------------------
  novoJogo: {
    gerandoRotulo: "Montando el mundo",
    gerandoTitulo: "Creando países, divisiones, clubes y plantillas",
    gerandoCorpo: "Más de 8.000 jugadores con nombre, contrato e historia propia.",

    rotulo: "Nueva carrera",
    titulo: "Elige tu club",
    resumoDaGeracao: "{clubes} clubes generados en {ms} ms.",
    seuNome: "Tu nombre",
    pais: "País",
    divisao: "División",
    divisaoOpcao: "{numero}ª división",
    colunas: {
      clube: "Club",
      reputacao: "Reputación",
      elenco: "Plantilla",
      caixa: "Fondos",
    },
    tambemVaoJogar: "También dirigen",
    assento: "{treinador} · {clube}",
    tirar: "quitar",
    selecione: "Elige un club de la lista.",
    clubeEscolhido: "{clube}, {liga}",
    adicionarTreinador: "Añadir otro entrenador",
    adicionarTreinadorDica: "Jugar con amigos en el mismo dispositivo",
    comecarComTreinadores: "Empezar con {total} entrenadores",
    assumirOComando: "Tomar el mando",
  },

  // -------------------------------------------------------------------------
  // Criador de clube, dentro da nova carreira
  // -------------------------------------------------------------------------
  criador: {
    titulo: "Nuevo club",
    escudo: "Escudo",

    nome: {
      rotulo: "Nombre del club",
      bloqueado: "Nombre no permitido.",
      duvidoso: "Nombre marcado para revisión.",
    },

    sigla: {
      // `Sigla` é a palavra que o resto do dicionário em espanhol já usa para a
      // abreviatura de país e de clube. Trocar aqui por `Siglas` só faria a
      // mesma coisa ter dois nomes na mesma carreira.
      rotulo: "Sigla",
      jaExiste: "Sigla ya usada en este mundo.",
      formato: "Tres letras, de la A a la Z.",
      sugestoes: "Libres",
      bloqueado: "Sigla no permitida.",
      duvidoso: "Sigla marcada para revisión.",
    },

    forma: "Forma",
    formas: {
      shield: "Escudo",
      circle: "Círculo",
      diamond: "Rombo",
      pennant: "Banderín",
    },

    padrao: "Patrón",
    padroes: {
      solid: "Liso",
      sash: "Banda",
      stripes: "Rayas",
      halves: "Mitades",
    },

    cores: {
      titulo: "Colores",
      fundo: "Fondo",
      segunda: "Secundario",
      contorno: "Borde",
    },

    catalogo: {
      titulo: "Figuras",
      abas: {
        cheios: "Rellenos",
        contornos: "Contornos",
      },
      busca: "Buscar figura",
      nadaEncontrado: "No se encontró nada.",
      todas: "Todas",
      categorias: {
        animais: "Animales",
        armas: "Armas",
        esporte: "Deporte",
        heraldica: "Heráldica",
        maquina: "Máquina",
        natureza: "Naturaleza",
        simbolos: "Símbolos",
      },
    },

    peca: {
      titulo: "Piezas",
      adicionar: "Añadir",
      remover: "Quitar",
      paraFrente: "Al frente",
      paraTras: "Atrás",
      girar: "Girar",
      tamanho: "Tamaño",
      cor: "Color",
      contagem: "{total} de {maximo}",
      limite: "Máximo de {maximo} piezas.",
      vazio: "Sin piezas",
    },

    previa: {
      titulo: "Vista previa",
      tabela: "Tabla",
      partida: "Partido",
      cabecalho: "Encabezado",
      grande: "Grande",
    },

    uniforme: {
      titulo: "Equipación",
      padrao: "Patrón",
      padroes: {
        solid: "Liso",
        stripes: "Rayas",
        sash: "Banda",
        hoops: "Rayas horizontales",
        halves: "Mitades",
        checks: "Cuadros",
      },
      cores: "Colores",
      corPrincipal: "Principal",
      corSecundaria: "Secundaria",
      peito: "Pieza en el pecho",
      semPeca: "Sin pieza",
    },

    confirmar: "Crear club",
    escudoDefinitivo: "El escudo no cambia después.",

    creditos: {
      titulo: "Créditos de arte",
      catalogo: "Catálogo",
      autor: "Autor",
      licenca: "Licencia",
      usadas: "Figuras utilizadas",
      figurasUsadas: "{total} figuras",
    },
  },

  // -------------------------------------------------------------------------
  // Hub do clube, incluindo a coluna de painéis da direita
  // -------------------------------------------------------------------------
  hub: {
    navAria: "Secciones del juego",
    reputacaoTecnico: {
      elite: "Élite",
      alta: "Alta",
      boa: "Buena",
      regular: "Regular",
      baixa: "Baja",
    },
    sair: "Salir de la carrera",
    nav: {
      elenco: "Plantilla",
      tatica: "Táctica",
      mercado: "Fichajes",
      competicoes: "Competiciones",
      clube: "Club",
      financas: "Finanzas",
      noticias: "Noticias",
      carreira: "Carrera",
      base: "Cantera",
      /* O botão que abre a folha no celular. */
      clubeFinancas: "Club / Finanzas",
      calendario: "Calendario",
      timeB: "Equipo B",
      relatorios: "Informes",
      configuracoes: "Configuración",
      mais: "Más",
    },
    abrirDados: "Ver los datos del club",
    fecharDados: "Ocultar los datos del club",
    fatos: {
      moral: "Moral",
      pontos: "Puntos",
      campanha: "V-E-D",
      treinador: "Entrenador",
      reputacaoTecnico: "Reputación",
      estadio: "Estadio",
      fundacao: "Fundación",
      caixa: "Fondos",
      objetivo: "Objetivo",
    },
    lugares: "{capacidade} localidades",
    preTemporada: "Pretemporada {temporada}",
    temporadaRodada: "Temporada {temporada} · jornada {rodada}/{total}",
    vezDe: "turno de",


    decisoesNaFila: "{total} decisión(es) en cola",
    atalhosDica: "Atajos de teclado",
    atalhosAria: "Ver los atajos de teclado",
    simulando: "Simulando...",
    proximaTemporada: "Empezar la próxima temporada",
    jogarRodada: "Jugar la jornada {rodada}",
    avancarRodada: "Avanzar jornada",
    filaTitulo: "En cola para la próxima jornada ({total})",

    temporadaEncerrada: "Temporada terminada.",
    semJogoMarcado: "Sin partido programado.",
    versus: "vs",
    emCasa: "En casa",
    foraDeCasa: "Fuera de casa",
    competicaoRodada: ", jornada {rodada}",
    competicaoFase: ", {fase}",
    classico: "Clásico",

    desempenho: "Rendimiento en la temporada",
    posicao: "Posición",
    posicaoValor: "{posicao}º",
    jogosDisputados: "{jogos} partido(s) jugado(s)",
    vitorias: "Victorias",
    empates: "Empates",
    derrotas: "Derrotas",
    saldo: "Diferencia",
    ultimosJogos: "Últimos partidos",

    financas: "Caja",
    saldoEmCaixa: "Saldo en caja",
    receitaPorRodada: "Ingresos por jornada",
    despesaPorRodada: "Gastos por jornada",
    salarios: "Salarios",
    moralExcelente: "Excelente",
    moralBoa: "Buena",
    moralRegular: "Regular",
    moralRuim: "Mala",
    moralValor: "{rotulo} ({valor})",

    rivalidades: "Rivalidades",
  },

  // -------------------------------------------------------------------------
  // Elenco
  // -------------------------------------------------------------------------
  elenco: {
    titulo: "Primer equipo",
    escalacao: {
      titular: "Titular",
      reserva: "Suplente",
      lesionado: "Lesionado · {rodadas}",
      suspenso: "Sancionado · {rodadas}",
      fora: "Fuera",
    },
    colunas: {
      tipo: "Tipo",
      escalacao: "Alineación",
      numero: "Nº",
      pos: "Pos",
      jogador: "Jugador",
      forca: "Fza",
      moral: "Moral",
      fisico: "Físico",
      gols: "Goles",
      situacao: "Estado",
      salario: "Salario",
    },
    lesionado: "lesionado {rodadas}",
    suspenso: "sancionado",
    linhaSecundaria: "{idade} años · {destaques}",
    titular: "Titular",
    banco: "Banquillo",
    fora: "Fuera",
  },

  // -------------------------------------------------------------------------
  // Cantera
  // -------------------------------------------------------------------------
  base: {
    titulo: "Cantera",
    turma: "{total} en la cantera",
    explicacao:
      "Estos son los chicos que se están formando. Su techo es invisible, como en el resto del juego: lo que ves es el rango en el que cree tu cuerpo técnico. A los 18 años, cada uno se convierte en una decisión tuya: subir al primer equipo, quedarse un año más o salir.",
    estrutura: "Cantera nivel {nivel}",
    oQueONivelCompra:
      "El nivel de la cantera decide cuántos chicos caben aquí, qué tan buenos llegan y qué tan estrecho es el rango de potencial.",
    vazia: "Todavía no hay nadie en la cantera. La próxima camada llega al cambiar la temporada.",

    decisaoTitulo: "Esperando tu decisión",
    decisaoDica: "Entra en la cola y se aplica cuando avances",
    promover: "Promover",
    manter: "Retener un año más",
    dispensar: "Dar de baja",
    prazo: "Puede quedarse en la cantera hasta la temporada {temporada}.",

    naBase: "En la cantera",
    esperandoTotal: "{total} esperando tu decisión",
    emFormacao: "En formación",
    emFormacaoTotal: "{total} sin edad para decidir",
    emFormacaoRotulo: "En formación",
    esperaDecisao: "Espera decisión",
    parado: "Dejó de crecer",
    naFilaPromover: "Promover, en cola",
    naFilaManter: "Retener, en cola",
    naFilaDispensar: "Dar de baja, en cola",

    anos: "{idade} años",
    faixa: "{baixo} a {alto}",
    potencialEstimado: "potencial estimado",
    evolucaoValor: "+{pontos}",

    colunas: {
      pos: "Pos",
      garoto: "Chico",
      idade: "Edad",
      forca: "Fza",
      evolucao: "Progreso",
      faixa: "Potencial",
      situacao: "Estado",
    },

    veredito: {
      vaiCrescer: "Tiene mucho por crecer y vale la apuesta.",
      quaseFeito: "Todavía mejora, pero ya casi es lo que será.",
      noTeto: "Está en su techo. Lo que ves es lo que te llevas.",
      estagnou: "Pasó el año parado, y su techo se encogió con él.",
    },

    confianca: {
      alta: "confianza alta",
      media: "confianza media",
      baixa: "confianza baja",
    },
  },

  // -------------------------------------------------------------------------
  // Tática
  // -------------------------------------------------------------------------
  tatica: {
    escalacao: "Alineación",
    forcaEmCampo: "fuerza en el campo {valor}",
    automatica: "Automática",
    formacao: "Formación",
    personalizada: "Personalizada",
    capitao: "Capitán",
    vagaAberta: "Hueco libre",
    improvisado: "improvisado en {pos}",
    naPosicao: "en su posición",
    condicaoDoTitular: "· {condicao}%",

    bancoDeReservas: "Suplentes ({total})",
    nenhumReserva: "Nadie en el banquillo.",
    foraDosRelacionados: "Fuera de la convocatoria",

    postura: "Actitud en el campo",
    estilo: "Estilo",
    estiloDefensivo: "Defensivo",
    estiloDefensivoAjuda: "Protege el resultado. Menos ataque, más defensa.",
    estiloEquilibrado: "Equilibrado",
    estiloEquilibradoAjuda: "Sin inclinarse hacia ningún lado.",
    estiloOfensivo: "Ofensivo",
    estiloOfensivoAjuda: "Va a por el partido. Crea más y encaja más.",
    marcacao: "Marcaje",
    marcacaoLeve: "Suave",
    marcacaoLeveAjuda: "Menos faltas y menos tarjetas, defensa más blanda.",
    marcacaoNormal: "Normal",
    marcacaoNormalAjuda: "Lo de siempre.",
    marcacaoForte: "Fuerte",
    marcacaoForteAjuda: "Mejor defensa, pero cansa al equipo y trae tarjetas.",
    ritmo: "Ritmo",
    ritmoBaixo: "Bajo",
    ritmoBaixoAjuda: "Cuida las piernas a cambio de algo de rendimiento.",
    ritmoNormal: "Normal",
    ritmoNormalAjuda: "Lo de siempre.",
    ritmoAlto: "Alto",
    ritmoAltoAjuda: "Rinde más en la primera parte. Cae en la segunda si falta fondo.",

    penaltis: "Penaltis",
    faltas: "Tiros libres",
    escanteios: "Córners",
    automatico: "Automático",

    avisoDaFila: "La alineación entra en cola y vale a partir de la próxima jornada.",
    confirmar: "Confirmar alineación",

    escolherPara: "Elegir jugador para {pos}",
    quemJogaAqui: "Quién juega aquí",
    idadeDoCandidato: "{idade} años",
    fisicoDoCandidato: "{condicao}% de físico",
    jaTitular: "ya es titular, cambiará de hueco",
    candidatoLesionado: "lesionado",
    candidatoSuspenso: "sancionado",

    forca: "Fuerza",
    confirmarCurto: "Confirmar",
    confirmada: "Confirmada",
    reservas: "Suplentes",
    abaBanco: "Banquillo",
    lugarVazio: "Lugar vacío",
    legenda: "Leyenda",
    legCondicao: "Condición",
    legImprovisado: "Improvisado",
    legCansado: "Cansado",
    legPendurado: "Apercibido",
    legLesionado: "Lesionado",
    legSuspenso: "Sancionado",
    setorAta: "Del",
    setorMei: "Med",
    setorDef: "Def",
    naVaga: "En el puesto",
    condicaoCurta: "Cond.",
    moral: "Moral",
    media: "Media",
    golsAssist: "G / A",
    posicaoEIdade: "{pos} · {idade} años",
    naturalEm: "{natural} de {vaga}",
    soltar: "Soltar",
    adversario: "Rival",
    posicaoEPontos: "{posicao}º · {pontos} pts",
    dueloAtaque: "Ataque x Def",
    dueloMeio: "Medio x Medio",
    dueloDefesa: "Defensa x Del",
    destaques: "Destacados",
    desfalques: "Bajas",
    destaqueGols: "{valor} goles",
    destaqueAssist: "{valor} asist.",
    destaqueCapitao: "capitán",
    tituloLesionado: "Lesionado: {rodadas} jornadas",
    tituloSuspenso: "Sancionado: {rodadas} jornadas",
    tituloImprovisado: "Improvisado, natural {pos}",
    tituloCansado: "Condición {valor}%",
    tituloPendurado: "Apercibido: {amarelos} amarillas",
    rotuloDaFicha: "{nome}, {pos}, nota {nota}, condición {condicao}%",
  },

  // -------------------------------------------------------------------------
  // Mercado
  // -------------------------------------------------------------------------
  mercado: {
    janelaFechada:
      "El mercado de fichajes está cerrado. Puedes mirar y armar la lista de objetivos, pero no fichar.",
    reabreNaRodada: "Vuelve a abrir en la jornada {rodada}.",
    reabreNaPreTemporada: "Vuelve a abrir en la pretemporada.",

    propostasRecebidas: "Ofertas recibidas ({total})",
    propostasEnviadas: "Ofertas enviadas ({total})",

    procurarJogador: "Buscar jugador",
    encontrados: "{total} encontrados",
    paginas: "Páginas",
    primeiraPagina: "Primera página",
    paginaAnterior: "Página anterior",
    proximaPagina: "Página siguiente",
    ultimaPagina: "Última página",
    paginaDe: "{pagina} de {paginas}",
    nomeDoJogador: "Nombre del jugador",
    todasAsPosicoes: "Todas las posiciones",
    qualquerIdade: "Cualquier edad",
    ateAnos: "hasta {idade} años",
    qualquerForca: "Cualquier fuerza",
    forcaOuMais: "fuerza {valor} o más",
    soOQueCabe: "Solo lo que cabe en la caja",
    abaProcurar: "Buscar",
    abaLivres: "Libres",

    colunas: {
      pos: "Pos",
      jogador: "Jugador",
      clube: "Club",
      forca: "Fza",
      preco: "Precio",
      pede: "Pide",
    },
    naLista: "en la lista",
    linhaSecundaria: "{idade} años · {destaques}",
    livre: "libre",
    naoVem: "no sale",
    nenhumJogador: "Ningún jugador con esos filtros.",

    jogadorResumo: "{pos}, fuerza {forca}",
    ofertaRecebida: "Oferta del {clube} por {valor}",
    ofertaEnviada: "Oferta al {clube} por {valor}",
    respondeEm: "responde en {rodadas} jornada(s)",
    aceitaNaFila: "Aceptada · en cola",
    recusadaNaFila: "Rechazada · en cola",
    recusar: "Rechazar",
    aceitar: "Aceptar",
    aguardando: "esperando",
  },

  // -------------------------------------------------------------------------
  // Competições
  // -------------------------------------------------------------------------
  competicoes: {
    abaEstadual: "Estatal",
    abaCopa: "Copa",
    abaContinental: "Continental",
    abaArtilharia: "Goleadores",
    abaCalendario: "Calendario",
    faixaDeConfrontos: "{de}–{ate} de {total}",
    confronto: {
      resultado: "Resultado",
      rodada: "Jornada",
      local: "Sede",
      naTabela: "En la tabla",
      outrosJogos: "Otros partidos",
      rodadaCurta: "J{rodada}",
    },

    campeao: "Campeón: {clube}",
    estadualResumo: "abre la temporada y junta las divisiones del estado",

    calendario: {
      resumo: "{jogos} partidos en la temporada, {classicos} de ellos clásicos",
      rodada: "Jornada {numero}",
      casa: "En casa",
      fora: "Fuera",
      neutro: "Campo neutral",
      proximo: "Próximo",
      vazio: "Ningún partido programado para esta temporada.",
      todos: "Todos",
      filtroAjuda: "Filtra por torneo para encontrar el partido que te interesa.",
    },

    /** Siglas de tabela em espanhol: PJ, G, E, P, GF, GC, DG, Pts. */
    colunas: {
      posicao: "#",
      clube: "Club",
      jogos: "PJ",
      vitorias: "G",
      empates: "E",
      derrotas: "P",
      golsPro: "GF",
      golsContra: "GC",
      saldo: "DG",
      pontos: "Pts",
    },

    seuGrupo: "tu grupo",
    tituloDaEdicao: "El título de esta edición fue para el {clube}.",
    doisPrimeiros:
      "Los dos primeros de cada grupo pasan a las eliminatorias. La franja verde marca quién pasaría hoy.",
    gruposEncerrados: "Fase de grupos terminada. El título se decide en las eliminatorias.",
    mostrarSoMeuGrupo: "Mostrar solo mi grupo",
    verOutrosGrupos: "Ver los otros {total} grupos",
    esconderGrupos: "Ocultar la fase de grupos",
    verGrupos: "Ver la fase de grupos",

    chaveamento: "Cuadro",
    copaNaoComecou:
      "La copa todavía no ha empezado. El sorteo de la primera ronda sale cuando llegue la jornada.",
    campanha: {
      naoClassificou: "Tu club no se clasificó para esta edición.",
      grupos: "En la fase de grupos.",
      gruposComMeu: "En la fase de grupos, en el {grupo}.",
      eliminadoNosGrupos: "Eliminado en la fase de grupos.",
      campeao: "Campeón de la {competicao}.",
      tituloDeOutro: "Eliminado. El título fue para el {clube}.",
      vivo: "Sigue vivo en la copa. Ronda actual: {fase}.",
      vivoSemFase: "Sigue vivo en la copa.",
      eliminado: "Eliminado. Última ronda: {fase}.",
      foraDaCopa: "Todavía no ha entrado en la copa.",
    },
    idaEVolta: "ida y vuelta",
    jogoUnico: "partido único",
    emDisputa: "en juego",
    maisConfrontos: "y {total} cruces más",
    ida: "ida",
    aDefinir: "Por definir",
    aDecidir: "por decidir",
    penaltis: "penaltis",
    agregado: "global",

    artilharia: "Goleadores",
    ninguemMarcou: "Todavía no ha marcado nadie esta temporada.",
    artilhariaDa: "Goleadores de {liga}",
    quantosNaLista: "{total} en la lista",
    colunasArtilharia: {
      posicao: "#",
      pos: "Pos",
      jogador: "Jugador",
      clube: "Club",
      jogos: "PJ",
      assistencias: "Asis",
      gols: "Goles",
    },
  },

  // -------------------------------------------------------------------------
  // Clube
  // -------------------------------------------------------------------------
  clube: {
    precoDoIngresso: "Precio de la entrada",
    explicacaoDoPreco:
      "Cobrar de más vacía el estadio y la taquilla cae con él. Los números de abajo son el público y los ingresos previstos contra un rival medio, con la misma cuenta que el juego hace en el partido.",
    estadioLota:
      "El estadio se llena al precio de hoy. Puedes cobrar hasta {preco} sin perder público, lo que daría {ganho} más por partido en casa.",
    colunas: {
      ingresso: "Entrada",
      publico: "Público previsto",
      ocupacao: "Ocupación",
      renda: "Ingresos por partido",
    },
    atual: "actual",
    melhorRenda: "mejores ingresos",
    cobrar: "Cobrar",

    estruturas: "Instalaciones",
    // Lo que cambia cada estructura. Estaba escrito a mano en
    // `competition-views.ts`, y aparecía en portugués debajo de un nombre de
    // estructura ya traducido.
    efeitoDaEstrutura: {
      stadium: "Capacidad y comodidad del estadio. Mueve el público y la recaudación.",
      youth:
        "Cuántos juveniles entran en la cantera, qué tan buenos llegan y qué tan estrecho es el rango de potencial que ve el cuerpo técnico.",
      training: "Velocidad de evolución del plantel y recuperación física entre jornadas.",
      medical: "Reduce el tiempo que un jugador queda afuera después de lesionarse.",
    },
    obraEmAndamento: "Obra en marcha",
    obraDescricao: "{nome} subiendo al nivel {nivel}. Faltan {rodadas} jornada(s).",
    manutencao: "mantenimiento {valor} por jornada",
    noMaximo: "al máximo",
    rodadasDeObra: "{rodadas} jornadas de obra",
    subirPara: "Subir a {nivel}",
    nivelAria: "nivel {nivel} de 5",

    parcelas: "Plazos de traspaso",
    parcelasResumo: "{aPagar} por pagar · {aReceber} por cobrar",
    colunasParcelas: {
      jogador: "Jugador",
      fluxo: "Flujo",
      venceEm: "Vence",
      valor: "Importe",
    },
    aReceber: "por cobrar",
    aPagar: "por pagar",
    nestaRodada: "en esta jornada",
    emRodadas: "{rodadas} jornada(s)",

    banco: "Banco",
    caixa: "Caja",
    divida: "Deuda",
    credito: "Crédito disponible",
    juros: "Intereses por jornada",
    jurosValor: "{taxa}%",
    explicacaoDoBanco:
      "El banco cobra intereses cada jornada sobre lo que debes. Con la caja en rojo mucho tiempo, la directiva vende un jugador sin consultar a nadie.",
    descontoDeParcelas: "El crédito ya descuenta {valor} de plazos de traspaso por vencer.",
    pegarEmprestado: "Pedir prestado",
    pedir: "Pedir",
    abaterDivida: "Reducir la deuda",
    pagar: "Pagar",
  },

  // -------------------------------------------------------------------------
  // Finanças
  // -------------------------------------------------------------------------
  financas: {
    titulo: "Caja",
    saldoEmCaixa: "Saldo en caja",
    receitaPorRodada: "Ingresos por jornada",
    despesaPorRodada: "Gastos por jornada",
    resultadoPorRodada: "Resultado por jornada",
    bilheteria: "Taquilla en la temporada",
    patrocinio: "Patrocinio en la temporada",
    tv: "Derechos de TV en la temporada",
    premiacao: "Premios en la temporada",
    folha: "Masa salarial por jornada",
    dividaComBanco: "Deuda con el banco",
    ingresso: "Entrada",
    capacidade: "Capacidad del estadio",
  },

  // -------------------------------------------------------------------------
  // Notícias
  // -------------------------------------------------------------------------
  noticias: {
    titulo: "Noticias",
    nadaNovo: "Nada nuevo por aquí.",
    rodada: "jornada {rodada}",
    compartilharContratacao: "Compartir el fichaje",
    contextoDoCartao: "Temporada {temporada} · jornada {rodada}",
    reforcoConfirmado: "Fichaje confirmado",
  },

  // -------------------------------------------------------------------------
  // Carreira
  // -------------------------------------------------------------------------
  carreira: {
    /*
     * O que a carreira diz quando a conversa com o servidor falha.
     *
     * Nenhuma destas frases interrompe a partida: a rodada já aconteceu no
     * aparelho e o mundo inteiro sobe no próximo envio.
     */
    servidor: {
      falhaNoEnvio:
        "No pude guardar tu carrera en el servidor ahora. La jornada sigue valiendo y lo intento de nuevo.",
      semRede:
        "No hablé con el servidor.",
      downloadIncompleto:
        "La carrera llegó incompleta.",
      arquivoIlegivel:
        "No pude leer la carrera que vino del servidor.",
    },

    resumo: "{temporadas} temporada(s) · {titulos} título(s) · reputación {reputacao}",
    carreiraComeca: "La carrera empieza ahora. Termina la primera temporada y aparecerá aquí.",
    colunas: {
      temporada: "Temp.",
      clube: "Club",
      competicao: "Competición",
      posicao: "Posición",
      titulos: "Títulos",
    },
    posicaoValor: "{posicao}º",

    bancoDeDados: "Base de datos",
    explicacaoDoBanco:
      'Guarda los clubes y las plantillas de esta carrera en un archivo. Puedes corregir un nombre en el bloc de notas, mandárselo a un amigo o empezar otra carrera con la misma base. Es el mismo archivo que la pantalla de inicio lee en "empezar con una base de datos propia".',
    salvarBanco: "Guardar base de datos",
    salvamento: "Guardado",
    salvarSozinho: "Guardar automáticamente en cada jornada",
    salvarAgora: "Guardar ahora",
    salvo: "Carrera guardada.",

    conquistas: "Logros",
    conquistasResumo: "{feitas} de {total}",
    temporadaDaConquista: "temp. {temporada}",
  },

  // -------------------------------------------------------------------------
  // Ficha do jogador
  // -------------------------------------------------------------------------
  ficha: {
    secaoCaracteristicas: "Atributos",
    abaProposta: "Oferta",
    abaEmprestimo: "Préstamo",
    abaQuePesam: "En la posición ({total})",
    abaDemais: "Demás ({total})",
    temporada: {
      jogos: "Partidos",
      gols: "Goles",
      assistencias: "Asistencias",
      cartoes: "Tarjetas",
    },
    aria: "Ficha de {nome}",
    subtitulo: "{clube} · {idade} años · {pe} · {personalidade}",
    fatos: {
      valor: "Valor",
      salario: "Salario",
      contratoAte: "Contrato hasta",
      naTemporada: "En la temporada",
      naCarreira: "En la carrera",
    },
    contratoValor: "temporada {temporada}",
    jogosEGols: "{jogos} partidos, {gols} goles",
    moral: "Moral",
    fisico: "Físico",
    notaMedia: "Nota media",

    olheiro: "Ojeador",
    confianca: "confianza {valor}",
    laudoResumo: "Potencial estimado entre {minimo} y {maximo}. {veredito}",
    confiancaAlta: "confianza alta",
    confiancaMedia: "confianza media",
    confiancaBaixa: "confianza baja",
    laudoAntigo: "informe viejo",
    faixa: "{baixo} a {alto}",
    potencialEstimado: "potencial estimado",
    observarDeNovo: "Volver a ojearlo por {valor}",
    semLaudo:
      "No sabes hasta dónde puede llegar. Un ojeador te devuelve un rango de potencial, y cuanto mejor sea tu cantera, más estrecho es ese rango.",
    mandarObservar: "Mandar ojearlo por {valor}",

    emprestimo: "Cedido por el {dono} al {tomador}. El {tomador} paga el {parte}% del salario.",


    elogiar: "Elogiar",
    darBronca: "Echar la bronca",
    renovarContrato: "Renovar contrato",
    tirarDaLista: "Quitar de la lista",
    listarParaVenda: "Poner como transferible",

    fazerProposta: "Hacer una oferta",
    leilao: "{clubes} clubes se pelean por este jugador. La puja más alta es {maiorLance}.",
    leilaoSeuLanceLidera: "Tu puja es la más alta hasta ahora.",
    leilaoPrecisaCobrir: "Para que te escuchen primero, hay que superarla.",
    naoDesceDePatamar: "{nome} no aceptaría bajar tanto de nivel. Ni merece la pena intentarlo.",
    explicacaoDaProposta:
      "El club empieza a escuchar a partir de {minimo}. Pide al menos {salario} por jornada. Tu caja es {caixa}.",
    valorDaCompra: "Importe del traspaso",
    salarioPorRodada: "Salario por jornada",
    anos: "Años",
    parcelas: "Plazos",
    aVista: "al contado",
    duasVezes: "2 plazos",
    tresVezes: "3 plazos",
    explicacaoDasParcelas:
      "Salen {entrada} ahora y el resto en {restantes} plazo(s) de {entrada}, cada 12 jornadas. El vendedor descuenta el pago aplazado al decidir, así que la oferta tiene que ser más alta para pesar lo mismo.",
    enviarProposta: "Enviar la oferta",
    comprarPor: "Comprar por {valor}",
    contratarAgora: "Fichar ahora",
    janelaFechada: "Mercado cerrado",

    ouPegarEmprestado: "O pedirlo cedido",
    explicacaoDoEmprestimo:
      "Juega contigo hasta el final de la temporada y luego vuelve a su club. No cuesta traspaso, pero asumes el {parte}% del salario, o sea {valor} por jornada.",
    pedirEmprestado: "Pedir la cesión",
  },

  // -------------------------------------------------------------------------
  // Resumo da partida
  // -------------------------------------------------------------------------
  adm: {
    proximoJogo: "Próximo partido",
    desempenho: "Rendimiento en la temporada",
    financas: "Finanzas",
    jogadorSelecionado: "Jugador seleccionado",
    abrirFicha: "Ficha",
    jogos: "PJ",
    vitorias: "G",
    empates: "E",
    derrotas: "P",
    pontos: "puntos",
    moralDoClube: "Moral del club",
    media: "Media",
    jogosNaTemporada: "Partidos",
    gols: "Goles",
    assistencias: "Asistencias",
    amarelos: "Amarillas",
    valor: "Valor",
    idadeEDestaques: "{idade} años · {destaques}",
  },
  relatorios: {
    abas: {
      posJogo: "Pospartido",
      rodada: "Jornada",
      elenco: "Plantilla",
      financeiro: "Finanzas",
    },
    semJogo: "Todavía no hay partidos esta temporada.",
    estatisticas: "Estadísticas",
    posse: "Posesión",
    chances: "Ocasiones",
    selecaoDaRodada: "Once de la jornada",
    destaques: "En forma",
    emQueda: "En baja",
    fisico: "Problemas físicos",
    moral: "Moral",
    moralMedia: "Moral media",
    insatisfeitos: "Descontentos",
    forcaMedia: "Fuerza media",
    entradas: "Ingresos de la temporada",
    saidas: "Por jornada",
    outrasDespesas: "Otros gastos",
    bilheteria: "Taquilla",
    patrocinio: "Patrocinio",
    tv: "Derechos de TV",
    premiacao: "Premios",
    receita: "Ingresos",
    folha: "Salarios",
  },
  saida: {
    titulo: "Salir de la carrera",
    gravadoEm: "Guardado · temporada {temporada}, jornada {rodada}",
    naoGravado: "Aún sin guardar",
    gravarESair: "Guardar y salir",
    sairSemGravar: "Salir sin guardar",
    voltaA: "Vuelve a la jornada {rodada}",
  },
  timeB: {
    titulo: "Equipo B",
    abas: {
      elenco: "Plantilla B",
      partida: "Último partido",
      classificacao: "Clasificación",
      historico: "Historial",
    },
    tecnico: "Técnico del equipo B",
    moral: "Moral",
    peneira: "Pruebas",
    peneiraValor: "Nivel {nivel} · pretemporada",
    chanceDeEstrela: "Probabilidad de Estrella",
    aspirantes: "Liga de Reservas",
    elencoB: "Plantilla B",
    trocarTecnico: "Cambiar técnico",
    ligaDe: "Liga de Reservas · {liga}",
    garoto: "Chico",
    colunas: {
      jogos: "PJ",
      gols: "Goles",
      feitos: "Logros",
      tecnico: "Técnico",
      estrela: "Estrella",
      salario: "Salario",
    },
    ultimoAno: "Último año",
    emFormacao: "En formación",
    ovr: "MED",
    linhaDoGaroto: "{posicao} · {idade} años · Equipo B",
    feitos: "Logros",
    numeros: {
      jogos: "Partidos",
      gols: "Goles",
      assistencias: "Asistencias",
      nota: "Nota media",
    },
    promover: "Subir ↑",
    vender: "Vender",
    dispensar: "Dejar libre",
    elencoCheio: "Plantilla principal llena",
    janelaFechada: "Mercado cerrado",
    dispensarConfirma: "{jogador} pasa a los libres.",
    propostas: "Ofertas recibidas",
    semPropostas: "Sin ofertas en esta jornada",
    aceitar: "Aceptar",
    recusar: "Rechazar",
    chutes: "Tiros",
    noGol: "A puerta",
    nomeDoB: "{clube} B",
    ligaERodada: "Liga de Reservas · {liga} · jornada {rodada}",
    lance: {
      gol: "Gol",
      amarelo: "Tarjeta amarilla",
      vermelho: "Tarjeta roja",
      lesao: "Lesión",
    },
    assistencia: "asistencia de {jogador}",
    rodadasFora: "{rodadas} jor. fuera",
    melhores: "Mejores del partido",
    campeao: "Campeón",
    historico: {
      temporada: "Temporada",
      posicao: "Posición",
      pontos: "Pts",
      artilheiro: "Goleador",
      subiram: "Subieron",
      peneira: "Pruebas",
    },
    emAndamento: "{posicao} · en curso",
    artilheiroValor: "{jogador} · {gols}",
    historicoDoTimeB: "Historial del equipo B",
    anoEmAndamento: "{ano} · en curso",
    peneiraDoAno: "Pruebas {ano}",
    chegaram: "{total} llegaron · nivel {nivel}",
    subiramAoPrincipal: "Subieron al primer equipo",
    atual: "Actual",
    contratarTecnico: "Contratar",
    multa: "Indemnización para despedir a {tecnico}",
    promoverTitulo: "Subir al primer equipo",
    linhaComFeitos: "{posicao} · {idade} años · {feitos} logros",
    estrela: "★ Estrella",
    jogadorEstrela: "★ Jugador Estrella",
    promovido: "Subió",
    potencial: "Potencial",
    moralDoElenco: "Moral de la plantilla",
    irAoElenco: "Ir a la plantilla",
  },
  livres: {
    titulo: "Libres",
    total: "{total} en el mercado",
    colunas: {
      origem: "Dejado libre por",
      livreHa: "Libre hace",
      sai: "Sale",
      pede: "Pide",
    },
    nestaRodada: "esta jornada",
    rodadas: "{rodadas} jor.",
    fimDaTemporada: "fin de temporada",
    emRodadas: "en {rodadas} jor.",
  },
  partida: {
    aria: "Resultado del partido",
    vitoria: "Victoria",
    empate: "Empate",
    derrota: "Derrota",
    classico: "Clásico",
    competicaoFase: ", {fase}",
    posse: "Posesión {valor}%",
    chances: "Ocasiones {minhas}-{dele}",
    porQueAconteceu: "Por qué pasó",
    lances: "Jugadas",
    minuto: "{minuto}'",
    compartilhar: "Compartir",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo
  // -------------------------------------------------------------------------
  aoVivo: {
    expandir: "Ampliar el partido",
    encolher: "Reducir el partido",
    abaLances: "Jugadas",
    abaRodada: "Jornada",
    jogoAcabou: "finalizado",
    aria: "Partido en directo",
    fimDeJogo: "final del partido",
    minuto: "{minuto}'",
    bolaVaiRolar: "El balón va a rodar.",

    intervalo: "Descanso",
    pausado: "En pausa",
    restamTrocas: "quedan {trocas} cambios",
    semReservas: "No hay suplentes disponibles.",
    emCampo: "En el campo",
    banco: "Banquillo",
    ninguemEmCampo: "Nadie en el campo.",
    voltarParaOJogo: "Volver al partido",

    pausar: "Pausar",
    ritmo1x: "1x",
    ritmo2x: "2x",
    ritmo4x: "4x",
    verResumo: "Ver el resumen de la jornada",
    pularParaResultado: "Saltar al resultado",
    condicaoDoJogador: "{condicao}%",
  },

  // -------------------------------------------------------------------------
  // Fim de temporada
  // -------------------------------------------------------------------------
  fimDeTemporada: {
    contextoDoCartao: "Temporada {temporada}",
    encerrada: "Temporada {temporada} terminada",
    posicaoValor: "{posicao}º",
    naLiga: "en {liga}",

    tituloConquistado: "Título conseguido",
    titulosConquistados: "Títulos conseguidos",
    compartilharTitulo: "Compartir el título",

    conquistaNova: "Logro desbloqueado",
    conquistasNovas: "Logros desbloqueados",
    verNaCarreira: "Los demás están en la pantalla de Carrera.",

    // El parecer de la dirigencia. Salió del save, donde era un párrafo congelado
    // en la redacción del día en que se grabó, y ahora se escribe acá a partir
    // del veredicto, la posición y los títulos.
    veredito: {
      demitido: "La dirigencia pedía esto: {objetivo}. El equipo terminó {posicao}º y el contrato no fue renovado.",
      titulo: "Un título es una respuesta que no necesita explicación. La dirigencia quiere más de lo mismo.",
      acima: "La dirigencia pedía esto: {objetivo}. Recibió más que eso, y tu crédito adentro creció.",
      cumpriu: "La dirigencia pedía esto: {objetivo}. El {posicao}º puesto sirve. Ni fiesta, ni reclamo.",
      abaixo: "La dirigencia pedía esto: {objetivo}. El {posicao}º puesto quedó por debajo, y otra temporada así cambia el tema.",
    },
    oQueADiretoriaAchou: "Lo que opinó la directiva",

    campanha: "Campaña",
    pontos: "Puntos",
    jogos: "Partidos",
    vitorias: "Victorias",
    empates: "Empates",
    derrotas: "Derrotas",
    saldo: "Diferencia",
    meta: "El objetivo era este: {meta}, es decir el puesto {posicao}.",

    caixaDaTemporada: "Caja de la temporada",
    receita: "Ingresos",
    despesa: "Gastos",
    resultado: "Resultado",
    caixaHoje: "Caja hoy",
    divida: "Deuda",

    destaques: "Destacados",
    artilheiro: "Máximo goleador",
    artilheiroValor: "{nome}, {gols} goles",
    ninguemDestaque: "Nadie marcó lo suficiente para destacar.",
    melhorNota: "Mejor nota",
    melhorNotaValor: "{nome}, {nota}",

    subiramDaBase: "Subieron de la cantera",
    garotos: "{total} canteranos",
    contratosAcabando: "Contratos que se acaban",
    renoveAntes: "renueva antes de que se vayan gratis",
    renovar: "Renovar",
    colunas: {
      pos: "Pos",
      jogador: "Jugador",
      idade: "Edad",
      forca: "Fuerza",
      salario: "Salario",
    },

    quemChamou: "Quién te ha llamado",
    sondagens: "Sondeos de otros clubes",
    escolhaOndeRecomecar: "elige dónde volver a empezar",
    aceitarTroca: "aceptar cambia tu club ahora mismo",
    ofertaLinha: "{liga} · {pais} · piden: {objetivo}",
    caixaDe: "caja de {valor}",
    assumir: "Asumir",
    verClube: "Ver club",
    previaCaixa: "Caja",
    previaDivida: "Deuda",
    previaFolha: "Nómina por jornada",
    previaEstadio: "Estadio",
    previaPosicao: "Posición",
    previaForca: "Fuerza de la plantilla",
    previaIdade: "Edad media",
    previaElenco: "Plantilla ({total})",
    previaAnos: "{idade} años",

    semClube: "Sin club",
    ninguemChamou: "Nadie te ha llamado esta vez. Empieza una carrera nueva cuando quieras.",
    escolhaUmClube: "Elige un club para seguir",
    comecarTemporada: "Empezar la temporada {temporada}",
  },

  // -------------------------------------------------------------------------
  // Conta
  // -------------------------------------------------------------------------
  conta: {
    chaveDoAlfa: "Clave de acceso alfa",
    chaveDoAlfaAjuda: "En el formato LFM-XXXX-XXXX.",
    erroEmail: "Correo no válido.",
    erroCodigo: "Código no válido.",
    erroSenhaVazada: "Esta contraseña ya apareció en una filtración pública. Elige otra.",
    confiraCampos: "Revisa: {campos}.",
    titulo: "Cuenta",
    voltar: "Volver al juego",

    /** Nome do aparelho na lista de dispositivos ativos. */
    aparelhos: {
      web: "Navegador",
      android: "Android",
      ios: "iPhone o iPad",
      desktop: "PC",
    },

    email: "Correo",
    senha: "Contraseña",
    novaSenha: "Contraseña nueva",
    nome: "Nombre visible",
    codigo: "Código que llegó por correo",
    senhaAjuda: "Al menos {minimo} caracteres.",
    nomeAjuda: "De {minimo} a {maximo} caracteres. Así es como apareces en las ligas.",

    entrar: "Entrar",
    entrando: "Entrando...",
    criarConta: "Crear cuenta",
    criando: "Creando...",
    naoTenhoConta: "Todavía no tengo cuenta",
    jaTenhoConta: "Ya tengo cuenta",
    esqueciASenha: "Olvidé mi contraseña",
    enviarLink: "Enviar el enlace",
    enviando: "Enviando...",
    redefinir: "Cambiar la contraseña",
    jaTenhoCodigo: "Ya tengo el código",
    voltarParaEntrar: "Volver a entrar",

    aceite:
      "Al crear la cuenta aceptas los términos de uso y la política de privacidad, en la versión de {versao}.",
    contaCriada:
      "Cuenta creada. Te mandamos un correo a {email}. Confirma la dirección y luego entra.",
    linkEnviado: "Si existe una cuenta con ese correo, el enlace de recuperación ya va de camino.",
    senhaTrocada: "Contraseña cambiada. Entra con la nueva.",

    sairDaConta: "Cerrar sesión",
    emailNaoConfirmado:
      "El correo todavía no está confirmado. Sin eso, el modo online y las compras quedan bloqueados.",
    reenviarConfirmacao: "Reenviar la confirmación",
    confirmacaoReenviada: "Mandamos otro enlace. Mira la bandeja de entrada.",
    contaDesde: "En casa desde {data}",

    perfil: "Perfil",
    salvar: "Guardar",
    salvando: "Guardando...",
    perfilSalvo: "Perfil guardado.",

    dispositivos: "Dispositivos activos",
    dispositivosAjuda:
      "Cada dispositivo que ha entrado en esta cuenta aparece aquí. ¿No reconoces alguno? Échalo y cambia la contraseña.",
    esteAparelho: "este dispositivo",
    ultimoUso: "último uso {quando}",
    entrouEm: "entró {quando}",
    derrubar: "Echar",
    semDispositivos: "Ningún dispositivo activo.",

    direitos: "Derechos y suscripción",
    vitalicia: "Licencia Vitalicia en Solitario",
    passe: "Pase Legacy",
    temDireito: "Activo",
    semDireito: "No lo tienes",
    renovaEm: "se renueva el {data}",
    semRenovacao: "sin renovación automática",
    porOrigem: "por {origem}",
    origens: {
      stripe: "tarjeta",
      pix: "Pix",
      play: "Google Play",
      appstore: "App Store",
      grant: "concesión",
    },
    cosmeticos: "Cosméticos",
    cosmeticosTotal: "{total} objeto(s)",

    dados: "Tus datos",

    excluirTitulo: "Eliminar la cuenta",
    excluirComoFunciona:
      "La cuenta queda marcada para eliminarse y pierdes el acceso al online al momento. Durante 7 días puedes cancelarlo entrando de nuevo. Al séptimo día borramos todo lo que se pueda borrar.",
    excluirOQueSai:
      "Lo que se va: correo, nombre, contraseña, avatar, sesiones, tokens de aviso, la carrera, bases de datos privadas, amistades y mensajes directos.",
    excluirOQueFica: "Lo que se queda, y por qué",
    excluirFicaFiscal: "Registro fiscal de compra: obligación legal de 5 años.",
    excluirFicaLigas:
      "Resultados y clasificaciones de las ligas que jugaste: son de todos los participantes, y tu nombre pasa a ser \"Entrenador eliminado\".",
    excluirFicaDatapacks:
      "Bases de datos públicas que publicaste: otras personas dependen de ellas, y la autoría pasa a ser anónima. Puedes despublicarlas antes.",
    excluirFicaSancao:
      "Registro de sanción: mientras dure, para que el castigo no se acabe con un clic.",
    excluir: "Eliminar mi cuenta",
    excluirConfirmar: "Escribe {palavra} abajo para confirmar.",
    excluirPalavra: "ELIMINAR",
    excluirAgora: "Confirmar la eliminación",
    exclusaoAgendada: "Eliminación marcada para el {data}. Hasta entonces, puedes cancelarla aquí mismo.",
    cancelarExclusao: "Cancelar la eliminación",
    exclusaoCancelada: "Eliminación cancelada. Tu cuenta sigue en su sitio.",
  },

  // -------------------------------------------------------------------------
  // Avisos no aparelho
  // -------------------------------------------------------------------------
  admin: {
    titulo: "Panel",
    voltar: "Volver al juego",
    semPermissao: "Esta área es solo para administradores.",
    precisaEntrar: "Inicia sesión para abrir el panel.",
    menu: {
      chaves: "Claves alfa",
      bandeiras: "Banderas",
    },
    chaves: {
      titulo: "Claves de acceso alfa",
      total: "Generadas",
      livres: "Libres",
      usadas: "Usadas",
      quantidade: "Cuántas generar",
      nota: "Nota",
      notaAjuda: "Para recordar a quién iba el lote. Opcional.",
      gerar: "Generar",
      gerando: "Generando...",
      copiar: "Copiar",
      copiada: "Copiada",
      revogar: "Revocar",
      chave: "Clave",
      estado: "Estado",
      quemUsou: "Quién la usó",
      livre: "Libre",
      usadaPor: "Usada por {nome}",
      usadaPorAlguem: "Usada",
      vazio: "Todavía no hay claves. Genera la primera.",
    },
    bandeiras: {
      titulo: "Banderas de funcionalidad",
      alfaFechado: "Alfa cerrada",
      alfaFechadoAjuda: "Encendida, crear cuenta exige clave de acceso.",
      ligada: "Encendida",
      desligada: "Apagada",
      vazio: "Ninguna bandera configurada.",
    },
  },
  notificacoes: {
    titulo: "Avisos",
    ajuda:
      "Elige qué merece un aviso en el dispositivo. Lo que apagues aquí sigue apareciendo en tu lista dentro del juego.",

    /* Nome de cada tipo. As chaves são as mesmas que a API guarda. */
    tipos: {
      deadline: "Se acerca el plazo de la jornada",
      roundProcessed: "Jornada procesada",
      offers: "Ofertas y subastas",
      chat: "Menciones en el chat de la liga",
      marketing: "Novedades de Legacy Football Manager",
    },
    tiposAjuda: {
      deadline: "Un recordatorio antes de que cierre la jornada, con tiempo para alinear.",
      roundProcessed: "El resultado en cuanto corre la jornada.",
      offers: "Cuando alguien puja por tu jugador o supera tu puja.",
      chat: "Solo cuando escriben tu nombre.",
      marketing: "Novedades y temporadas nuevas. Nunca publicidad de terceros.",
    },

    salvando: "Guardando...",
    salvo: "Preferencias guardadas.",

    /* O convite, no saguão da liga assíncrona. */
    convite: {
      titulo: "¿Quieres que te avisemos cuando corra la jornada?",
      corpo:
        "Esta liga corre sola, a la hora acordada. Sin aviso puedes perder la alineación y enterarte del resultado al día siguiente.",
      sim: "Quiero que me avisen",
      agoraNao: "Ahora no",
      pedindo: "Esperando al navegador...",
    },

    /* O que aparece depois da resposta do navegador. */
    ativados: "Listo. Vas a recibir el aviso de la jornada en este dispositivo.",
    negados:
      "Sin problema. Puedes activarlo luego en los ajustes de notificación del navegador.",
    indisponiveis: "Este dispositivo no recibe avisos del navegador.",
    semServidor: "Este servidor no tiene configurados los avisos en el dispositivo.",
    falhou: "No pude activar los avisos ahora. Puedes intentarlo más tarde.",
    jaAtivos: "Los avisos de este dispositivo están activados.",
    bloqueados:
      "Los avisos están bloqueados para el juego en este navegador. Para recuperarlos, permítelos en los ajustes del sitio.",
  },

  // -------------------------------------------------------------------------
  // Ligas online
  // -------------------------------------------------------------------------
  opcoes: {
    carreira: "Carrera",
    som: {
      titulo: "Sonido",
      gol: "Sonido de gol en el partido en vivo",
    },
    titulo: "Opciones",
    voltar: "Volver",

    tema: {
      titulo: "Tema",
      escuro: "Oscuro",
      claro: "Claro",
      brasao: "Escudo",
      semClube: "Disponible con una carrera abierta.",
    },
    idioma: {
      titulo: "Idioma",
      ajuda:
        "Vale para este dispositivo al instante. Si tienes cuenta, la elección sube a ella y el siguiente dispositivo ya abre así.",
    },

    avisos: {
      precisaDeConta:
        "Los avisos se guardan en tu cuenta, una elección por persona. Entra en tu cuenta para elegir cuáles quieres recibir.",
    },


    erros: {
      titulo: "Informe de errores",
      enviar: "Enviar un informe cuando el juego se rompa",
      oQueVai:
        "Va lo que se rompió, la versión del juego, la plataforma, la semilla del mundo y el número de la jornada.",
      saveNuncaVai:
        "Tu partida guardada nunca se envía. La semilla y la jornada bastan para reproducir el problema, porque la simulación es determinista.",
      semEndereco:
        "Esta versión del juego se compiló sin destino para informes, así que no hay nada que enviar de todos modos.",
    },


    sobre: {
      titulo: "Acerca de",
      versaoDoJogo: "Versión del juego",
      versaoDoMotor: "Versión del motor",
      versaoDaApi: "Versión de la API",
      protocolo: "Protocolo",
      motorDoServidor: "El servidor está ejecutando el motor {versao}.",
      licencas: "Licencias",
      licencaDoJogo:
        "Legacy Football Manager se distribuye bajo licencia de uso personal. Estas son las bibliotecas de código abierto que carga con él.",
    },
  },

  ligas: {
    titulo: "Ligas online",
    voltar: "Volver",
    atualizar: "Actualizar",
    carregarMais: "Cargar más",

    abas: {
      minhas: "Mis ligas",
      publicas: "Ligas públicas",
      criar: "Crear liga",
    },

    semMinhas: "Todavía no estás en ninguna liga. Entra por invitación o crea la tuya.",
    semPublicas: "Ninguna liga pública abierta ahora mismo.",

    entrarPorCodigo: "Entrar con código de invitación",
    codigo: "Código",
    codigoAjuda: "De 6 a 12 letras y números, como te lo pasó el anfitrión.",
    entrar: "Entrar",
    entrou: "Listo, ya estás en {liga}.",

    estados: {
      lobby: "En la sala",
      running: "En marcha",
      paused: "Pausada",
      finished: "Terminada",
      stalled: "Atascada",
    },
    visibilidades: {
      private: "Privada",
      public: "Pública",
    },

    linha: {
      temporadaRodada: "Temporada {temporada} · jornada {rodada}",
      treinadores: "{humanos} de {max} entrenadores",
      prazo: "Plazo en {quando}",
      semPrazo: "Sin jornada programada",
      meuClube: "Tu club: {clube}",
      abrir: "Abrir",
    },

    criar: {
      titulo: "Crear liga",
      exigePasse: "Abrir una liga forma parte del Pase Legacy.",
      nome: "Nombre de la liga",
      visibilidade: "Quién puede entrar",
      privada: "Solo quien tenga el código",
      publica: "Cualquiera, desde el escaparate",
      maxHumanos: "Máximo de entrenadores",
      paises: "Países",
      paisesAjuda: "De 1 a 8 países. Cada uno entra con las divisiones elegidas abajo.",
      paisesNomes: {
        BR: "Brasil",
        AR: "Argentina",
        ES: "España",
        EN: "Inglaterra",
        IT: "Italia",
        PT: "Portugal",
      },
      divisoes: "Divisiones por país",
      divisoesOpcao: "{numero} división(es)",
      ritmo: "Ritmo",
      ritmoAsync: "Por plazo",
      ritmoAsyncAjuda:
        "La jornada corre a la hora marcada. Quien no mandó decisión juega con la alineación anterior.",
      ritmoLive: "En directo",
      ritmoLiveAjuda:
        "La jornada corre cuando todos marcan listo, respetando la espera máxima.",
      cron: "Cuándo corre la jornada",
      cronAjuda: "Expresión cron de cinco campos, en la zona horaria elegida.",
      fuso: "Zona horaria",
      esperaAoVivo: "Espera por los ausentes, en minutos",
      temporadas: "Temporadas",
      distribuicao: "Cómo se reparten los clubes",
      distribuicaoSorteio: "Sorteo",
      distribuicaoDraft: "Draft",
      dificuldade: "Dificultad",
      dificuldades: {
        facil: "Fácil",
        normal: "Normal",
        dificil: "Difícil",
      },
      mercado: "Mercado entre humanos",
      mercadoLigado: "Permitir negocios entre entrenadores humanos",
      razaoMinima: "Oferta mínima, respecto al valor de mercado",
      razaoMinimaValor: "{valor}%",
      negociosPorDupla: "Negocios por pareja de clubes, por temporada",
      aprovacao: "El comisario aprueba cada negocio",
      criar: "Crear la liga",
      criando: "Creando...",
      escolhaUmPais: "Elige al menos un país.",
    },

    saguao: {
      membros: "Entrenadores ({total} de {max})",
      comissario: "comisario",
      semClube: "club por definir",
      online: "en línea",
      offline: "desconectado",
      pronto: "decisión enviada",
      semDecisao: "sin decisión en esta jornada",
      ultimaAcao: "última acción {quando}",
      situacoes: {
        active: "activo",
        left: "se fue",
        kicked: "expulsado",
      },
      prazo: "Plazo",
      convite: "Código de invitación",
      copiar: "Copiar",
      copiado: "Código copiado.",
      gerarConvite: "Generar otro código",
      conviteExpira: "Vale hasta {data}",
      comecar: "Empezar la liga",
      comecando: "Empezando...",
      comecarAjuda:
        "Solo el comisario la empieza. A partir de ahí no entra nadie más y se reparten los clubes.",
      sair: "Salir de la liga",
      expulsar: "Expulsar",
      pausar: "Pausar",
      retomar: "Reanudar",
      forcarRodada: "Correr la jornada ahora",
      rodadaPedida: "Jornada {rodada} pedida. El servidor la procesa en un momento.",
      abrirHub: "Entrar en la liga",
      configuracao: "Configuración",
      resumo:
        "{paises} · {divisoes} división(es) por país · {temporadas} temporada(s) · dificultad {dificuldade}",
      mercadoLigado: "Mercado entre humanos activado, mínimo del {razao}% del valor.",
      mercadoDesligado: "Sin negocios entre entrenadores humanos.",
    },
  },

  // -------------------------------------------------------------------------
  // Hub da liga online
  // -------------------------------------------------------------------------
  hubOnline: {
    assistir: "Ver el partido",
    proximoJogo: "Próximo partido",
    navAria: "Secciones de la liga",
    sair: "Salir de la liga",
    nav: {
      elenco: "Plantilla",
      tabela: "Clasificación",
      noticias: "Noticias",
      treinadores: "Entrenadores",
      chat: "Chat",
    },

    prazoRotulo: "Plazo de la jornada",
    semPrazo: "Sin jornada programada",
    prazoVencido: "Plazo vencido. La jornada puede correr en cualquier momento.",
    processando: "La jornada {rodada} se está procesando. Las decisiones están bloqueadas.",
    pausada: "Liga pausada por el comisario.",
    encerrada: "Liga terminada.",
    travada: "La liga se ha atascado y necesita al comisario.",
    avisoDePrazo: "Faltan {minutos} minuto(s) para el plazo de la jornada {rodada}.",

    temporadaRodada: "Temporada {temporada} · jornada {rodada}/{total}",
    caixa: "Caja",
    divida: "Deuda",
    estadio: "Estadio",
    lugares: "{capacidade} localidades",
    objetivo: "Objetivo",

    semJogoMarcado: "Sin partido en esta jornada.",
    emCasa: "En casa",
    foraDeCasa: "Fuera de casa",
    versus: "vs",

    decisoes: "Decisiones de la jornada",
    prontos: "{prontos} de {total} listos",
    estouPronto: "Marcar mi decisión como enviada",
    aindaNao: "Desmarcar",
    decisoesAjuda:
      "En ritmo en directo, la jornada corre en cuanto todos marcan. En ritmo por plazo, la marca sirve para que los demás sepan que ya resolviste lo tuyo.",

    elenco: "Plantilla",
    elencoColunas: {
      numero: "Nº",
      pos: "Pos",
      jogador: "Jugador",
      forca: "Fza",
      moral: "Moral",
      fisico: "Físico",
      salario: "Salario",
    },
    lesionado: "lesionado {rodadas}",
    suspenso: "sancionado",
    linhaSecundaria: "{idade} años · {destaques}",

    tabela: "Clasificación",
    semTabela: "La clasificación aparece cuando corra la primera jornada.",
    noticias: "Noticias",
    semNoticias: "Nada nuevo por aquí.",
    noticiaRodada: "jornada {rodada}",
    treinadores: "Entrenadores",

    chat: "Chat de la liga",
    chatVazio: "Nadie ha dicho nada todavía.",
    chatCampo: "Escribe un mensaje",
    chatEnviar: "Enviar",
    chatDigitando: "alguien está escribiendo...",
    chatSemHistorico:
      "El historial no está disponible en este servidor. Los mensajes nuevos aparecen aquí.",
    chatSistema: "Aviso de la liga",
    chatDesconectado: "Sin conexión con la liga. El mensaje sale cuando vuelva.",

    resultado: "Jornada {rodada}: {casa} {golsCasa}-{golsFora} {fora}",
    posicaoAgora: "Estás {posicao}º en la clasificación.",
    atualizar: "Actualizar",
    semClube: "Todavía no tienes club en esta liga. Por ahora solo puedes seguirla.",
  },

  // -------------------------------------------------------------------------
  // Partida ao vivo pelo servidor
  // -------------------------------------------------------------------------
  partidaOnline: {
    aria: "Partido en directo de la liga",
    conectando: "Entrando en el canal del partido...",
    esperando: "El balón va a rodar.",
    minuto: "{minuto}'",
    fimDeJogo: "final del partido",
    desconectado: "Se cayó la conexión. El partido vuelve cuando ella vuelva.",
    fechar: "Cerrar",

    pausa: "Pausa para el cambio",
    janela: "ventana {janela} de 3",
    tempoParaDecidir: "{segundos}s para decidir",
    semReservas: "No hay suplentes en el banquillo.",
    emCampo: "En el campo",
    banco: "Banquillo",
    ninguemEmCampo: "Nadie en el campo.",
    trocaEnviada: "Cambio enviado. Vale en el resultado oficial de la jornada.",
    seguirSemMexer: "Seguir sin tocar nada",
    semClubeNaPartida: "Estás viendo como espectador, así que no puedes hacer cambios.",

    posse: "Posesión",
    chances: "Ocasiones",
    finalizacoes: "Tiros",
    cartoes: "Tarjetas",
    porQueAconteceu: "Por qué pasó",
  },

  // -------------------------------------------------------------------------
  // Editor de banco de dados
  // -------------------------------------------------------------------------
  editor: {
    titulo: "Editor de base de datos",

    acoes: {
      abrir: "Abrir archivo",
      gerar: "Generar base",
      branco: "Empezar en blanco",
      salvar: "Guardar archivo",
      jogar: "Jugar la jornada y seguir el partido",
      sair: "Salir del editor",
    },

    estado: {
      gerando: "Montando una base nueva. Tarda unos segundos.",
      semBancoTitulo: "Ninguna base abierta",
      semBancoCorpo:
        "Abre un archivo que ya tengas, genera una base completa con los 320 clubes o empieza en blanco y monta la tuya desde cero.",
      aberto: "Abierta en {ms} ms",
      /** Contagens do cabeçalho, cada uma com singular e plural. */
      contagem: {
        paisUm: "1 país",
        paisMuitos: "{total} países",
        divisaoUm: "1 división",
        divisaoMuitos: "{total} divisiones",
        clubeUm: "1 club",
        clubeMuitos: "{total} clubes",
        jogadorUm: "1 jugador",
        jogadorMuitos: "{total} jugadores",
      },
      salvo: "Archivo guardado como {nome}",
    },

    erro: {
      arquivo: "No pude leer este archivo. Tiene que ser un JSON de base de datos.",
      semBanco: "Abre o genera una base antes.",
      invalido:
        "La base todavía tiene {total} problemas. Resuélvelos antes de empezar la carrera, o el juego rechaza el archivo.",
    },

    abas: {
      estrutura: "Estructura",
      jogadores: "Jugadores",
      massa: "En masa",
    },

    arquivo: {
      titulo: "Archivo",
      nome: "Nombre de la base",
      autor: "Autor",
      notas: "Observaciones",
      notasAjuda: "Añade aquí la fuente de los datos y lo que cambió en esta versión.",
    },

    arvore: {
      titulo: "Países y divisiones",
      novoPais: "Nuevo país",
      novaDivisao: "Nueva división",
      novoClube: "Nuevo club",
      clubeUm: "1 club",
      clubes: "{total} clubes",
    },
    /** Nome que um item novo recebe antes de alguém batizá-lo. Vira conteúdo do
     * arquivo, então nasce na língua de quem está editando. */
    padroes: {
      banco: "Base nueva",
      pais: "País nuevo",
      divisao: "División",
      clube: "Club",
      estado: "Estado",
      copa: "Copa",
    },

    pais: {
      titulo: "País",
      nome: "Nombre",
      sigla: "Sigla",
      siglaAjuda: "Hasta cuatro letras y única en el archivo. Es la que liga jugador y país.",
      adjetivo: "Adjetivo",
      adjetivoAjuda: "Como en Brasileño. Aparece en los textos de noticia y de selección.",
      forca: "Fuerza del país",
      forcaAjuda: "De 1 a 5. Mide el peso del fútbol del país: premios, afición y cotización.",
      copa: "Nombre de la copa",
      estados: "Estados",
      estadosAjuda: "Uno por línea. El club sin estado se queda fuera del estatal.",
      divisoes: "Divisiones",
      remover: "Quitar país",
    },

    divisao: {
      titulo: "División",
      nome: "Nombre",
      nivel: "Nivel",
      nivelAjuda: "1 es la élite. El nivel ordena el ascenso entre las divisiones del país.",
      acesso: "Suben",
      acessoAjuda: "Cuántos clubes suben a la división de arriba al final de la temporada.",
      queda: "Bajan",
      quedaAjuda: "Cuántos clubes bajan a la división de abajo.",
      clubes: "Clubes de la división",
      remover: "Quitar división",
    },

    clube: {
      titulo: "Club",
      nome: "Nombre",
      sigla: "Sigla",
      siglaAjuda: "Tres letras, como aparece en el escudo y en la clasificación.",
      fundacao: "Fundación",
      cores: "Colores",
      corPrincipal: "Color principal",
      corSecundaria: "Color secundario",
      reputacao: "Reputación",
      reputacaoAjuda:
        "De 1 a 6. Tira del patrocinio, de la afición y de quién acepta jugar en el club.",
      torcida: "Afición",
      capacidade: "Capacidad del estadio",
      ingresso: "Precio de la entrada",
      caixa: "Caja",
      estado: "Juega el estatal",
      semEstado: "Sin estatal",
      estruturas: "Instalaciones",
      elenco: "Plantilla",
      elencoResumo: "{jogadores} · {goleiros} · fuerza {forca}",
      goleiroUm: "1 portero",
      goleiroMuitos: "{total} porteros",
      semGoleiro: "ningún portero",
      novoJogador: "Nuevo jugador",
      duplicar: "Duplicar club",
      mover: "Mover de división",
      moverAjuda: "Lleva el club con toda la plantilla a otra división del mismo país.",
      remover: "Quitar club",
    },

    estruturas: {
      stadium: "Estadio",
      youth: "Cantera",
      training: "Entrenamiento",
      medical: "Servicios médicos",
    },

    reputacoes: {
      um: "1 Municipal",
      dois: "2 Estatal",
      tres: "3 Regional",
      quatro: "4 Nacional",
      cinco: "5 Continental",
      seis: "6 Mundial",
    },

    jogador: {
      titulo: "Jugador",
      nome: "Nombre",
      nacionalidade: "Nacionalidad",
      nacionalidadeAjuda: "La sigla del país, como BR. Es lo que define su selección.",
      posicao: "Posición",
      idade: "Edad",
      pe: "Pie",
      atributos: "Atributos",
      atributosAjuda:
        "Los catorce del motor. El porcentaje al lado del nombre es su peso en la posición actual.",
      potencial: "Potencial",
      potencialAjuda: "Techo de fuerza. Nunca queda por debajo de la fuerza de hoy.",
      personalidade: "Personalidad",
      lesao: "Propensión a la lesión",
      salario: "Salario por jornada",
      contrato: "Años de contrato",
      forca: "Fuerza",
      forcaDerivada:
        "La fuerza no se edita. Sale de los atributos que pesa la posición, con la misma cuenta que hace el motor, y cambia sola cuando los tocas.",
      voltar: "Volver al club",
      remover: "Quitar jugador",
    },

    pes: {
      D: "Derecho",
      E: "Izquierdo",
      A: "Ambos",
    },

    personalidades: {
      lider: "Líder",
      profissional: "Profesional",
      ambicioso: "Ambicioso",
      temperamental: "Temperamental",
      mercenario: "Mercenario",
      tranquilo: "Tranquilo",
    },

    lesoes: {
      baixa: "Baja",
      media: "Media",
      alta: "Alta",
    },

    jogadores: {
      titulo: "Jugadores de la base",
      procurar: "Buscar por nombre",
      todasAsPosicoes: "Todas las posiciones",
      todosOsPaises: "Todos los países",
      encontrados: "{total} de {geral}",
      vazio: "Ningún jugador con ese filtro.",
      colunas: {
        nome: "Nombre",
        pos: "Pos",
        idade: "Edad",
        forca: "Fuerza",
        clube: "Club",
      },
    },

    massa: {
      titulo: "Operaciones en masa",
      renomear: "Renombrar en lote",
      procurar: "Buscar",
      substituir: "Sustituir por",
      alvo: "Dónde cambiar",
      escopo: "En qué país",
      todosOsPaises: "Todos los países",
      previa: "Se cambiarían {total} nombres",
      previaUm: "Se cambiaría 1 nombre",
      previaNenhum: "Ningún nombre con ese texto",
      aplicar: "Cambiarlo todo",
      aviso:
        "La búsqueda distingue mayúsculas de minúsculas y cambia todas las apariciones dentro del nombre.",
      feito: "{total} nombres cambiados.",
      feitoUm: "1 nombre cambiado.",
      alvos: {
        clubes: "Nombres de clubes",
        jogadores: "Nombres de jugadores",
        ambos: "Clubes y jugadores",
      },
      atalhos:
        "Duplicar y mover club están en la pantalla del club, junto al resto de la edición.",
    },

    problemas: {
      titulo: "Problemas",
      nenhum: "Ningún problema. Esta base abre carrera.",
      contagem: "{total} problemas",
      um: "1 problema",
      ajuda: "Haz clic en un problema para abrir el elemento que hay que arreglar.",
      raiz: "archivo",
    },

    lugar: {
      pais: "País {numero}",
      divisao: "División {numero}",
      clube: "Club {numero}",
      jogador: "Jugador {numero}",
    },
  },

  // -------------------------------------------------------------------------
  // Ajuda dos atalhos de teclado
  // -------------------------------------------------------------------------
  atalhos: {
    aria: "Atajos de teclado",
    rotulo: "Teclado",
    titulo: "Atajos",
    fecheComEsc: "ciérralo con Esc",
    explicacao:
      "La jornada entera cabe en el teclado. Mientras escribes en un campo, ninguna tecla es un atajo: Esc sale del campo y le devuelve el teclado al juego. Con un panel abierto, solo valen las teclas de ese panel.",
    emBreve: "próximamente",
    voltarAoJogo: "Volver al juego",

    ligacaoAte: "a",

    grupos: {
      rodada: "Jornada",
      secoes: "Secciones",
      navegacao: "Navegación",
      hotseat: "Hotseat",
      aoVivo: "En el partido en directo",
      painelAberto: "Con un panel abierto",
    },

    descricoes: {
      calendario: "Calendario",
      timeB: "Equipo B",
      relatorios: "Informes",
      jogar: "Jugar la jornada",
      elenco: "Plantilla",
      tatica: "Táctica",
      mercado: "Fichajes",
      clube: "Club",
      competicoes: "Competiciones",
      noticias: "Noticias",
      financas: "Caja",
      carreira: "Carrera",
      busca: "Buscar jugador: abre los fichajes con el cursor en la búsqueda",
      lista: "Moverse por la lista de la pantalla",
      abrir: "Abrir la fila marcada",
      voltar: "Cerrar el panel abierto o volver a la plantilla",
      ajuda: "Mostrar esta lista",
      assento: "Pasar el turno a otro entrenador del hotseat",
      pausar: "Pausar o seguir el partido",
      ritmo: "Ritmo 1x, 2x o 4x",
      pular: "Saltar al resultado",
      fecharPainel: "Cerrar el panel de arriba",
      continuar: "Continuar en el resumen del partido y al final de temporada",
      cartao: "Generar la tarjeta para compartir",
    },
  },

  // -------------------------------------------------------------------------
  // Loja
  // -------------------------------------------------------------------------
  loja: {
    titulo: "Tienda",
    voltar: "Volver",
    atualizar: "Actualizar",
    carregarMais: "Cargar más",

    /*
     * Servidor sem credencial de provedor. Não é falha de quem está jogando,
     * é estado desta instalação, e o texto precisa dizer isso sem alarme.
     */
    semPagamento: "Las compras todavía no están disponibles en este servidor.",
    semPagamentoCorpo:
      "Quien cuida de este servidor todavía no ha conectado un medio de pago. El catálogo se queda aquí para consultar, y lo que ya compraste sigue valiendo.",
    semPagamentoMetodo: "El medio pedido fue {metodo}.",

    catalogo: "Lo que se puede comprar",
    semProdutos: "Este servidor no tiene nada en el catálogo por ahora.",

    grupos: {
      lifetime: "Licencia vitalicia",
      passe: "Pase Legacy",
      cosmetic: "Cosméticos",
      datapack: "Bases de datos",
    },

    tipos: {
      lifetime: "Compra única",
      pass_month: "Suscripción mensual",
      pass_year: "Suscripción anual",
      cosmetic: "Objeto cosmético",
      datapack: "Base de datos",
    },

    duracaoUmMes: "Un mes de acceso por cobro",
    duracaoMeses: "{meses} meses de acceso por cobro",
    semPrazo: "Sin fecha de caducidad",
    semPreco: "Sin precio en {moeda}",
    jaTem: "Ya lo tienes",

    comprarCartao: "Pagar con tarjeta",
    comprarPix: "Pagar con Pix",
    abrindo: "Abriendo...",
    metodoFora: "Este servidor no ofrece pago por {metodo}.",
    metodos: {
      card: "tarjeta",
      pix: "Pix",
      android: "tienda de Android",
      ios: "tienda de Apple",
    },

    checkoutAberto: "Abrimos la página de pago en otra pestaña. Termina allí y vuelve aquí.",
    checkoutSemAba:
      "El navegador bloqueó la pestaña nueva. Usa el botón de abajo para abrir el pago.",
    abrirPagamento: "Abrir el pago",
    fecharCobranca: "Cerrar",

    pixTitulo: "Pix de {valor}",
    pixInstrucao: "Apunta la cámara al código o usa el copia y pega.",
    pixCopiar: "Copiar el código",
    pixCopiado: "Código copiado.",
    pixExpira: "Este código vale hasta {data}.",
    pixEspera: "En cuanto entre el pago, el derecho aparece solo en tu cuenta.",

    recibos: "Tus recibos",
    semRecibos: "Ninguna compra registrada todavía.",
    reciboLinha: "{situacao} · {origem} · {data}",
    situacoes: {
      pending: "esperando el pago",
      paid: "pagado",
      refunded: "devuelto",
      expired: "vencido",
    },

    assinatura: "Suscripción",
    assinaturaAtiva: "Pase Legacy activo.",
    assinaturaRenova: "Se renueva el {data}.",
    assinaturaSemRenovacao: "Sin renovación automática.",
    assinaturaSemPasse: "No tienes una suscripción activa que cancelar.",
    cancelar: "Cancelar la renovación",
    cancelando: "Cancelando...",
    cancelarAjuda:
      "Cancelar es dejar de renovar, no perder lo ya pagado: el pase vale hasta el final del periodo.",
    cancelado: "Renovación cancelada en {total} suscripción(es).",
    canceladoAte: "El pase sigue valiendo hasta {data}.",
    cancelarNaLoja:
      "Esta suscripción se hizo dentro de la tienda del dispositivo, y es ahí donde termina. Abre las suscripciones de la tienda para acabar.",

    restaurar: "Restaurar compras",
    restaurando: "Comprobando...",
    restaurado: "Derechos comprobados con el servidor.",
    restaurarAjuda:
      "¿Compraste en otro dispositivo, en la web o en la tienda? Esto vuelve a leer en el servidor los derechos de esta cuenta.",

    vestiario: "Tu vestuario",
    vestiarioAjuda:
      "Elige qué usar en cada sitio. Puedes cambiarlo cuando quieras, y cambiar no cuesta nada.",
    vestiarioVazio: "Todavía no tienes ningún cosmético. Lo que compres aquí aparece en esta lista.",
    vestiarioErro: "No pude cargar tu vestuario.",
    usar: "Usar",
    emUso: "En uso",
    tirar: "Quitar",
    trocando: "Cambiando...",
    trocado: "Listo, el aspecto ha cambiado.",
    ondeAparece: {
      crest: "Aparece en el escudo de tu club, en la carrera y en las ligas online.",
      kit: "Aparece al lado del escudo, en la cabecera de tu club.",
      stadium: "Pinta la cabecera del club con los colores del estadio.",
      title: "Aparece al lado de tu nombre en la lista de entrenadores de las ligas.",
    },
  },

  // -------------------------------------------------------------------------
  // Cosméticos
  // -------------------------------------------------------------------------
  cosmeticos: {
    tipos: {
      crest: "Escudo",
      kit: "Equipación",
      stadium: "Tema de estadio",
      title: "Título de entrenador",
    },
    // Sigla que aparece dentro do escudo na vitrine, onde ainda não existe
    // clube nenhum para emprestar a dele.
    siglaDaPrevia: "LM",
    tituloAjuda: "Título de entrenador, comprado en la tienda. No cambia nada en el campo.",
  },

  // -------------------------------------------------------------------------
  // Mercado entre treinadores humanos
  // -------------------------------------------------------------------------
  mercadoOnline: {
    titulo: "Mercado de la liga",
    voltar: "Volver",
    atualizar: "Actualizar",
    carregarMais: "Cargar más",

    semClube: "Todavía no tienes club en esta liga, así que no hay nada que negociar.",
    mercadoDesligado: "Esta liga no permite negocios entre entrenadores.",

    abas: {
      recebidas: "Recibidas",
      enviadas: "Enviadas",
      leiloes: "Subastas",
      historico: "Historial",
    },

    filtros: {
      abertas: "Abiertas",
      aguardando: "Esperando al comisario",
      todas: "Todas",
      leiloesAbertos: "Abiertas",
      leiloesFechados: "Cerradas",
      leiloesTodos: "Todas",
    },

    situacoes: {
      open: "abierta",
      accepted: "aceptada",
      rejected: "rechazada",
      countered: "contraofertada",
      expired: "vencida",
      vetoed: "vetada",
    },

    direcoes: {
      in: "te llegó",
      out: "la propusiste tú",
    },

    semPropostas: "Ninguna oferta por aquí.",
    semLeiloes: "Ninguna subasta por aquí.",

    linhaValor: "Importe",
    linhaParcelas: "Plazos",
    linhaPrazo: "Cierra en la jornada {rodada}",
    linhaPrazoVencido: "Plazo vencido en la jornada {rodada}",
    linhaTroca: "Con {jogador} en el trato",
    linhaEmprestimo: "Cesión hasta la temporada {temporada}, con el {percentual}% del salario",
    linhaContraproposta: "La contraoferta pide {valor}",
    naFila: "Ya está en la cola de esta jornada",
    esperandoComissario: "Esperando el visto bueno del comisario",

    aceitar: "Aceptar",
    recusar: "Rechazar",
    contrapor: "Contraofertar",
    enviando: "Enviando...",

    /*
     * A contraproposta troca os lados da mesa, e isso muda quem responde.
     * Quem não entende isso acha que contrapôs e ficou esperando o outro,
     * quando na verdade agora é ele quem está à espera.
     */
    contraporTitulo: "Contraofertar: la mesa cambia de lado",
    contraporExplicacao:
      "La oferta que llegó se cierra y nace una tuya, en sentido contrario. A partir de ahora quien responde es el otro entrenador, y el plazo vuelve a empezar.",
    contraporValor: "Cuánto quieres",
    contraporEnviar: "Enviar la contraoferta",
    contrapostaEnviada:
      "Contraoferta enviada. La oferta original quedó {situacao}, y ahora quien responde es el otro entrenador.",

    aceitaNaFila: "Trato cerrado. El traspaso entra en la jornada {rodada}.",
    aceitaEsperando:
      "Trato cerrado entre los dos. Falta el visto bueno del comisario para entrar en la jornada.",
    recusadaOk: "Oferta rechazada.",

    rodada: "Jornada",
    situacaoDaLiga: "Situación",
    avalDoComissario: "Visto bueno del comisario",
    avalExigido: "Exigido",
    avalDispensado: "No hace falta",

    aprovar: "Liberar",
    aprovada: "Liberada. El traspaso entra en la jornada {rodada}.",
    vetar: "Vetar",
    vetoMotivo: "Motivo del veto, si quieres decirlo",
    vetoEnviar: "Confirmar el veto",
    vetada: "Oferta vetada, y el traspaso salió de la cola.",
    aprovacaoLigada:
      "En esta liga todo negocio pasa por el comisario antes de entrar en la jornada.",

    leilaoLanceMinimo: "Puja mínima",
    leilaoMeuLance: "Tu puja",
    leilaoSemLance: "Todavía no has pujado",
    leilaoClubes: "{total} club(es) en la disputa",
    leilaoFechado: "Cerrada con {valor}",
    leilaoSemVencedor: "Cerrada sin ganador",
    leilaoLance: "Cuánto pagas",
    leilaoDarLance: "Pujar",
    leilaoLanceFeito: "Puja de {valor} registrada. Se cierra en la jornada {rodada}.",
    leilaoFechadoAjuda:
      "La puja es a ciegas: ves cuántos clubes entraron y tu propio importe, nunca el de los demás. Quién gana se decide al cambiar la jornada.",

    novaTitulo: "Hacer una oferta",
    novaJogador: "Jugador",
    novaEscolha: "Elige un jugador de otro entrenador",
    novaBusca: "Buscar por el nombre",
    novaSemJogadores: "Ningún jugador de otro entrenador está disponible ahora.",
    naLista: "en la lista",
    novaValor: "Cuánto pagas",
    novaParcelas: "En cuántos plazos",
    novaParcela: "{total}x",
    novaEnviar: "Enviar la oferta",
    novaEnviada: "Oferta enviada al {clube}.",
    novaVirouLeilao:
      "Otro club también quiere a este jugador, así que la disputa se convirtió en subasta. Tu oferta entró como puja.",
    novaPiso: "El suelo de esta liga es el {percentual}% del valor del jugador.",
  },

  // -------------------------------------------------------------------------
  // Ranqueado
  // -------------------------------------------------------------------------
  ranqueado: {
    titulo: "Clasificatoria",
    voltar: "Volver",
    atualizar: "Actualizar",
    carregarMais: "Cargar más",

    temporada: "Temporada {numero}",
    temporadaSituacao: "Situación",
    temporadaEstados: {
      upcoming: "todavía va a empezar",
      running: "en marcha",
      finished: "terminada",
    },
    temporadaPeriodo: "Del {inicio} al {fim}",
    temporadaSemanas: "{semanas} semanas",
    temporadaTreinadores: "{total} entrenadores por liga",

    meuMmr: "Tu MMR",
    minhaDivisao: "Tu división",
    divisoes: "Divisiones",
    divisaoFaixa: "a partir de {mmr} de MMR",

    fila: "Cola",
    filaAjuda:
      "La liga se monta en cuanto haya gente suficiente con MMR parecido. Puedes salir de la cola en cualquier momento.",
    entrarNaFila: "Entrar en la cola",
    entrando: "Entrando...",
    sairDaFila: "Salir de la cola",
    saindo: "Saliendo...",
    naFila: "Estás en la cola desde {quando}.",
    naFilaPosicao: "Puesto {posicao} de {total} en la región {regiao}.",
    naFilaFaltam: "Faltan {quantos} para cerrar una liga.",
    naFilaCompleta: "Hay gente suficiente en la cola. La liga sale en la próxima entrada.",
    foraDaFila: "No estás en la cola.",
    saiuDaFila: "Saliste de la cola.",
    regiao: "Región",

    ligaPronta: "Tu liga clasificatoria está montada.",
    abrirLiga: "Abrir la liga",
    emDisputa: "Temporada en curso",
    minhaEntrada: "División {divisao}, MMR de entrada {mmr}.",
    minhaPosicao: "Puesto {posicao} en la clasificación.",
    semPosicaoAinda: "El puesto final sale al terminar la temporada.",
    semEntrada: "Todavía no juegas una liga en esta temporada.",

    /*
     * O ranqueado é do Passe, e o servidor recusa a fila com
     * `ENTITLEMENT_REQUIRED`. A tela não esconde o modo: mostra a temporada e o
     * leaderboard, e diz na hora certa o que falta.
     */
    exigePasse: "La cola clasificatoria forma parte del Pase Legacy.",
    irParaLoja: "Verlo en la tienda",

    leaderboard: "Clasificación general",
    leaderboardTodas: "Todas las divisiones",
    semLeaderboard: "Nadie ha puntuado en esta temporada todavía.",
    colunaPosicao: "#",
    colunaTreinador: "Entrenador",
    colunaMmr: "MMR",
    colunaDivisao: "División",
    colunaNaLiga: "En la clasificación",
    voce: "tú",

    historico: "Historial",
    semHistorico: "Ninguna temporada clasificatoria jugada todavía.",
    historicoLinha: "Temporada {numero} · división {divisao}",
    historicoVariacao: "MMR de {inicio} a {fim}",
    historicoSemFim: "MMR de entrada {inicio}, temporada todavía abierta",
    historicoPosicao: "Terminó en el puesto {posicao}.",
  },



  // -------------------------------------------------------------------------
  // Texto que viene del motor
  // -------------------------------------------------------------------------
  jornal: {
    titulo: "Noticias",
    mundo: "Mundo",
    preTemporada: "Pretemporada",
    rodada: "Jornada {rodada}",
    edicao: "Temporada {temporada} · {quando}",
    vazio: "Todavía no hay noticias.",
    emAlta: "Lo más visto",
    artilharia: "Goleadores",
    selecaoDaRodada: "Once de la jornada",
    mercado: "Mercado de la temporada",
    semClube: "Sin club",
    verFicha: "Ficha de {jogador}",
    editorias: {
      resultado: "Resultado",
      tecnicos: "Técnicos",
      mercado: "Mercado",
      lesoes: "Lesiones",
      estadios: "Estadios",
      numeros: "Números",
      selecao: "Selección",
      financas: "Finanzas",
      competicao: "Competición",
      especulacao: "Rumores",
    },
    arte: {
      publico: "espectadores",
      divida: "de deuda",
      media: "por partido",
    },
    fontes: {
      f1: "Rumor",
      f2: "Rumor",
      f3: "Fuente fiable",
      f4: "Fuente oficial",
    },
    selos: {
      confirmado: "Confirmado",
      desmentido: "No se confirmó",
    },
    noSeuClube: "En tu club",
    efeitos: {
      moral: "Moral de la plantilla {delta}",
      moralOutro: "Moral de {clube} {delta}",
      diretoria: "Paciencia de la directiva {delta}",
      publico: "Público {delta}%",
      valor: "Valor de {jogador} {delta}%",
      moralJogador: "Moral de {jogador} {delta}",
    },
  },

  sino: {
    titulo: "Notificaciones",
    vazio: "Nada por aquí.",
    abas: {
      mercado: "Mercado",
      elenco: "Plantilla",
      diretoria: "Directiva",
      competicao: "Competiciones",
    },
  },

  coletiva: {
    rotulo: "Rueda de prensa",
    responder: "Responder",
    semComentarios: "Sin comentarios",
    perguntas: {
      vitoria: "Gran resultado. ¿{clube} ya puede soñar a lo grande esta temporada?",
      derrota: "Momento difícil. ¿Su trabajo está en riesgo?",
    },
    respostas: {
      vitoria: {
        r0: "Partido a partido. El grupo sabe adónde quiere llegar.",
        r1: "Esta es la mejor plantilla del país, y hoy quedó claro.",
        r2: "El rival habló demasiado. La respuesta llegó en el campo.",
      },
      derrota: {
        r0: "La responsabilidad es mía. Voy a encontrar el camino.",
        r1: "La plantilla tiene que dar más, y me va a oír.",
        r2: "No fue nuestro día. La pelota no quiso entrar.",
      },
    },
    veiculos: {
      v0: "Radio Capital",
      v1: "TV Deporte",
      v2: "Diario de la Ciudad",
      v3: "Portal del Balón",
      v4: "Radio Nacional",
      v5: "Canal del Hincha",
    },
  },

  motor: {
    manchetes: {
      especulacao: {
        monitora: {
          titulo: "{interessado} sigue de cerca a {jogador}, de {clube}",
          fina: "{jogador} ({posicao}, {idade} años) vale {valor} en el mercado.",
          corpo: "La información circula entre bastidores. {clube} no comentó el interés.",
        },
      },
      coletiva: {
        vitoria: {
          humilde: {
            titulo: "{tecnico} evita la euforia: \"Partido a partido\"",
            fina: "El entrenador de {clube} habló en rueda de prensa tras el partido.",
            corpo: "Pese al buen momento, {tecnico} prefirió mantener los pies en el suelo.",
          },
          confiante: {
            titulo: "\"La mejor plantilla del país\": {tecnico} se declara favorito",
            fina: "El entrenador de {clube} subió el tono en rueda de prensa.",
            corpo: "La declaración anima a la plantilla, y la directiva ahora espera más.",
          },
          provocar: {
            titulo: "{tecnico} provoca al rival: \"Hablaron demasiado\"",
            fina: "El entrenador de {clube} no ahorró críticas al rival.",
            corpo: "A la afición le gustó, y el rival lo recordará en el próximo encuentro.",
          },
        },
        derrota: {
          culpa: {
            titulo: "{tecnico} asume la culpa: \"La responsabilidad es mía\"",
            fina: "El entrenador de {clube} protegió a la plantilla.",
            corpo: "La postura gustó a la directiva, que sigue con el entrenador.",
          },
          cobrar: {
            titulo: "{tecnico} exige a la plantilla: \"Tiene que dar más\"",
            fina: "El entrenador de {clube} no ocultó su enfado en rueda de prensa.",
            corpo: "La directiva aprobó la exigencia; en el vestuario, el ambiente se tensó.",
          },
          azar: {
            titulo: "{tecnico} culpa a la mala suerte: \"No fue nuestro día\"",
            fina: "El entrenador de {clube} restó importancia al resultado.",
            corpo: "A la plantilla le gustó el tono; a la directiva, no tanto.",
          },
        },
      },
      resultado: {
        viradaNoFim: {
          titulo: "{vencedor} remonta ante {perdedor} en el final: {golsVencedor} a {golsPerdedor}",
          fina: "{autor} lo decidió en el minuto {minuto}.",
          corpo: "{competicao}: {vencedor} empezó perdiendo y buscó la victoria en los minutos finales. El gol de {autor}, en el {minuto}, cerró el partido {golsVencedor} a {golsPerdedor} ante {perdedor}.",
        },
        golNoFim: {
          titulo: "Gol en el {minuto}: {vencedor} vence a {perdedor} por {golsVencedor} a {golsPerdedor}",
          fina: "{autor} marcó el gol de la victoria en el final.",
          corpo: "{competicao}: el partido iba hacia el empate hasta que apareció {autor} en el minuto {minuto}. {vencedor} venció a {perdedor} por {golsVencedor} a {golsPerdedor}.",
        },
        virada: {
          titulo: "{vencedor} da vuelta al partido y vence a {perdedor} por {golsVencedor} a {golsPerdedor}",
          fina: "{autor} marcó el gol de la remontada en el minuto {minuto}.",
          corpo: "{competicao}: {perdedor} se adelantó, pero {vencedor} reaccionó. El gol que lo puso por delante fue de {autor}, en el {minuto}, y el partido terminó {golsVencedor} a {golsPerdedor}.",
        },
        zebra: {
          titulo: "Sorpresa: {vencedor} tumba a {perdedor} por {golsVencedor} a {golsPerdedor}",
          fina: "{competicao}: el club más modesto venció al favorito.",
          corpo: "Por la diferencia entre los dos clubes, fue el resultado más inesperado de la jornada. Marcador final: {vencedor} {golsVencedor} x {golsPerdedor} {perdedor}.",
        },
        goleada: {
          titulo: "{vencedor} arrolla a {perdedor}: {golsVencedor} a {golsPerdedor}",
          fina: "{autor} marcó {golsDoAutor} de los {golsVencedor} goles.",
          corpo: "{competicao}: {vencedor} dominó de principio a fin ante {perdedor}. {autor} fue la figura del partido.",
        },
        classicoVitoria: {
          titulo: "{vencedor} gana el clásico ante {perdedor} por {golsVencedor} a {golsPerdedor}",
          fina: "{rivalidade} · {competicao}",
          corpo: "En el clásico, ganó {vencedor}: {golsVencedor} a {golsPerdedor} sobre {perdedor}. Para la afición, la victoria vale más que tres puntos.",
        },
        classicoEmpate: {
          titulo: "El clásico termina en empate: {mandante} {gols} x {gols} {visitante}",
          fina: "{rivalidade} · {competicao}",
          corpo: "{mandante} y {visitante} empataron, y esta vez nadie se lleva la rivalidad a casa.",
        },
        novoLider: {
          titulo: "{clube} asume el liderato",
          fina: "{competicao}: suma {pontos} puntos tras la jornada {rodada}.",
          corpo: "{clube} superó a {anterior}, que era líder hasta la jornada anterior.",
        },
        sequenciaDeVitorias: {
          titulo: "{clube} gana su {vitorias}ª victoria seguida",
          fina: "{competicao}: el equipo es el {colocacao}º clasificado.",
          corpo: "Son {vitorias} victorias consecutivas, contando todas las competiciones.",
        },
      },
      tecnicos: {
        jejumNoZ4: {
          titulo: "{jogos} partidos sin ganar: el {clube} de {tecnico} cae a la zona de descenso",
          fina: "{competicao}: el equipo es el {colocacao}º clasificado.",
          corpo: "La racha aumenta la presión sobre {tecnico}. La directiva sigue la situación de cerca.",
        },
        jejum: {
          titulo: "Presión sobre {tecnico}: {clube} suma {jogos} partidos sin ganar",
          fina: "{competicao}: el equipo es el {colocacao}º clasificado.",
          corpo: "La última victoria fue hace {jogos} partidos, contando todas las competiciones.",
        },
        troca: {
          titulo: "{clube} cambia de entrenador: sale {saiu}, llega {entrou}",
          fina: "{saiu} deja el club tras terminar {colocacao}º en la temporada {temporada}.",
          corpo: "La directiva no renovó la confianza en el proyecto. {entrou} dirige al equipo la próxima temporada.",
        },
      },
      mercado: {
        transferencia: {
          titulo: "{para} ficha a {jogador} por {valor}",
          fina: "{jogador} ({posicao}, {idade} años) deja {de}.",
          corpo: "{de} recibe {valor} por la venta.",
        },
        maiorVendaDaTemporada: {
          titulo: "Mayor venta de la temporada: {jogador} deja {de} por {valor}",
          fina: "{jogador} ({posicao}, {idade} años) firma con {para}.",
          corpo: "Ningún traspaso de la temporada había llegado a esa cifra hasta ahora.",
        },
        joiaVendida: {
          titulo: "{de} vende a la joya {jogador}, de {idade} años, por {valor}",
          fina: "El chico ({posicao}) se va a {para}.",
          corpo: "El club se queda con el dinero ahora; {para} apuesta por el futuro.",
        },
        contratacaoLivre: {
          titulo: "{para} ficha a {jogador} sin pagar traspaso",
          fina: "{jogador} ({posicao}, {idade} años) estaba sin club.",
          corpo: "Con fuerza {forca}, llega como refuerzo sin coste de traspaso.",
        },
      },
      lesoes: {
        lesaoGrave: {
          titulo: "{jogador} se lesiona y será baja de {clube} durante {rodadas} jornadas",
          fina: "{posicao} · goles en la temporada: {gols}",
          corpo: "{clube} pierde una pieza importante: la previsión es de {rodadas} jornadas fuera.",
        },
      },
      estadios: {
        recorde: {
          titulo: "Con {publico} espectadores, {clube} bate el récord de público del estadio",
          fina: "El récord anterior era de {anterior}. El estadio tiene {capacidade} localidades.",
          corpo: "El público récord llegó en el partido contra {adversario}.",
        },
        mediaDePublico: {
          titulo: "{divisao}: {clube} tiene más público de media que {superados} clubes de la élite",
          fina: "Son {media} espectadores por partido, por encima de {superados} clubes de {divisaoDeCima}.",
          corpo: "El ranking considera los partidos en casa de la temporada.",
        },
      },
      numeros: {
        marcaDeGols: {
          titulo: "{jogador} llega a {gols} goles en la temporada",
          fina: "{clube} · marca alcanzada en la jornada {rodada}",
          corpo: "{jogador} está entre los máximos goleadores de la temporada.",
        },
        hatTrick: {
          titulo: "{jogador} marca {gols} goles y lidera a {clube} ante {adversario}",
          fina: "Marcador final: {clube} {golsClube} x {golsAdversario} {adversario}",
          corpo: "Es el tipo de noche que un goleador guarda para siempre.",
        },
      },
      selecao: {
        convocacao: {
          titulo: "{selecao} convoca a {total} jugadores para la Copa de las Naciones",
          fina: "{jogador}, de {idade} años, es el más joven de la lista.",
          corpo: "El benjamín del grupo juega en {clube}.",
        },
        campea: {
          titulo: "{selecao} gana la Copa de las Naciones",
          fina: "El torneo vuelve dentro de {temporadas} temporadas.",
          corpo: "Es la mejor selección del mundo hasta la próxima edición.",
        },
      },
      financas: {
        noVermelho: {
          titulo: "{clube} entra en números rojos",
          fina: "Caja de {caixa}, con una deuda de {divida}.",
          corpo: "Con la caja en negativo, la directiva puede verse obligada a vender jugadores para pagar las cuentas.",
        },
      },
      competicao: {
        campeao: {
          titulo: "{competicao}: {clube} es campeón",
          fina: "Fueron {pontos} puntos en {jogos} partidos en la temporada {temporada}.",
          corpo: "El título corona la campaña y trae el mayor premio de la competición.",
        },
        campeaoDaCopa: {
          titulo: "{competicao}: {clube} levanta la copa",
          fina: "El título llega con el premio de la final.",
          corpo: "{clube} superó todas las rondas eliminatorias hasta la conquista.",
        },
        acesso: {
          titulo: "{clube} consigue el ascenso",
          fina: "La próxima temporada, el equipo juega en {divisao}.",
          corpo: "Es la recompensa de una campaña entre los primeros clasificados.",
        },
        rebaixamento: {
          titulo: "{clube} desciende",
          fina: "La próxima temporada, el equipo juega en {divisao}.",
          corpo: "El descenso obliga al club a empezar de nuevo una división más abajo, con menos ingresos.",
        },
      },
    },
    noticias: {
      resultado: {
        classicoVitoria: {
          titulo: "Clásico: victoria ante el {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} por el {torneio}. La hinchada va a recordar esta victoria ante el {adversarioNome} durante algunas semanas.",
        },
        classicoVitoriaGrande: {
          titulo: "Clásico: victoria ante el {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} por el {torneio}. La hinchada va a recordar esta victoria ante el {adversarioNome} durante mucho tiempo.",
        },
        classicoEmpate: {
          titulo: "Clásico: empate con el {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} por el {torneio}. Contra el {adversarioNome} nadie se va contento con un empate.",
        },
        classicoDerrota: {
          titulo: "Clásico: derrota ante el {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} por el {torneio}. Perder con el {adversarioNome} cuesta más caro que los tres puntos.",
        },
        classicoDerrotaGrande: {
          titulo: "Clásico: derrota ante el {adversario}",
          corpo:
            "{rivalidade}. {mandante} {golsMandante}-{golsVisitante} {visitante} por el {torneio}. Perder con el {adversarioNome} cuesta muchísimo más caro que los tres puntos.",
        },
      },

      elenco: {
        suspensaoPorCartoes: {
          titulo: "Sanción por amarillas",
          corpo: "{jogador} llegó a {amarelos} amarillas y se pierde la próxima jornada.",
        },
        deVoltaDaSelecao: {
          titulo: "De vuelta de la selección",
          corpo: "{jogador} volvió de la selección y está a disposición.",
        },
        climaPesado: {
          titulo: "Mal ambiente en el vestuario",
          corpo:
            "{primeiro} y {segundo} discutieron en el entrenamiento. El plantel tiene demasiadas figuras y ningún líder que sostenga al grupo.",
        },
        pedidoDeSaida: {
          titulo: "Pidió salir",
          corpo: "{jogador} pidió dejar el club y quedó en la lista de transferibles.",
        },
        escalacaoRecusada: {
          titulo: "Alineación rechazada",
          corpo: "La alineación no fue aceptada: {motivo}",
        },
        conversaComOElenco: {
          titulo: "Charla con el plantel",
          corpo: "Del vestuario: {recado}",
        },
        renovacaoRecusada: {
          titulo: "Renovación rechazada",
          corpo: "{jogador} consideró baja la oferta y rechazó la renovación.",
        },
        dispensa: {
          titulo: "Rescisión",
          corpo: "{jogador} quedó libre. La rescisión costó {valor}.",
        },
        elogioAceito: {
          titulo: "Charla con el plantel",
          corpo: "{jogador} agradeció el elogio.",
        },
        broncaMalRecebida: {
          titulo: "Charla con el plantel",
          corpo: "A {jogador} no le gustó nada el reto.",
        },
        broncaAceita: {
          titulo: "Charla con el plantel",
          corpo: "{jogador} escuchó el reto y prometió respuesta dentro de la cancha.",
        },
        garotoDeEmergencia: {
          titulo: "Juvenil ascendido de urgencia",
          corpo: "Con el plantel corto, {jogador} subió de las inferiores.",
        },
        aposentadoria: {
          titulo: "Retiro",
          corpo:
            "{jogador} colgó los botines a los {idade} años, tras {jogos} partidos y {gols} goles.",
        },
        basePedeDecisaoUm: {
          titulo: "La cantera pide una decisión",
          corpo:
            "{jogador} ya tiene edad para subir. Promuévelo, reténlo un año más o dale la baja antes de que empiece la temporada.",
        },
        basePedeDecisao: {
          titulo: "La cantera pide decisiones",
          corpo:
            "{total} chicos ya tienen edad para subir: {jogadores}. Promuévelos, reténlos un año más o dales la baja antes de que empiece la temporada.",
        },
        basePerdeuGaroto: {
          titulo: "La cantera perdió a un chico",
          corpo: "{jogador} cumplió {idade} años sin subir y se fue a buscar fútbol a otro club.",
        },
        promovidoDaBase: {
          titulo: "Promovido de la cantera",
          corpo: "{jogador} firmó con el primer equipo y pasa a ocupar plaza en la plantilla.",
        },
        dispensadoDaBase: {
          titulo: "Baja en la cantera",
          corpo: "{jogador} quedó libre y ya no forma parte del club.",
        },
        baseRecusou: {
          titulo: "La cantera esperaba otra cosa",
          corpo: "La decisión no se aplicó: {motivo}",
        },
        baseViraLivre: {
          titulo: "Dejó el equipo B",
          corpo: "{jogador} cumplió {idade} años y pasó a los libres.",
        },
        jogadorEstrela: {
          titulo: "¡Jugador Estrella!",
          corpo: "{jogador} subió del equipo B como Jugador Estrella. La plantilla ganó moral.",
        },
        vendidoDaBase: {
          titulo: "Venta del equipo B",
          corpo: "{jogador} fue vendido a {clube} por {valor}.",
        },
        tecnicoDaBaseContratado: {
          titulo: "Nuevo técnico en el equipo B",
          corpo: "{tecnico} asumió el equipo B. La indemnización del anterior costó {multa}.",
        },
      },

      lesao: {
        jogadorLesionado: {
          titulo: "Jugador lesionado",
          corpo: "{jogador} se lesionó y queda fuera alrededor de {rodadas} jornada(s).",
        },
        recuperado: {
          titulo: "Alta médica",
          corpo: "{jogador} ya está en condiciones de jugar.",
        },
      },

      financeiro: {
        obraConcluida: {
          titulo: "Obra terminada",
          corpo: "La obra quedó lista y la instalación llegó al nivel {nivel}.",
        },
        obraEmAndamento: {
          titulo: "Obra en marcha",
          corpo: "Termina la obra actual antes de empezar otra.",
        },
        obraInvalida: {
          titulo: "Obra no permitida",
          corpo: "Las instalaciones suben de a un nivel por vez.",
        },
        obraInviavel: {
          titulo: "Obra fuera de alcance",
          corpo: "La obra cuesta {custo} y la caja no alcanza.",
        },
        emprestimoNegado: {
          titulo: "Préstamo denegado",
          corpo: "El banco no lo autorizó: {motivo}",
        },
      },

      diretoria: {
        vendaForcada: {
          titulo: "Venta forzada",
          corpo:
            "Con la caja en rojo, la dirigencia vendió a {jogador} por {valor} sin consultar al cuerpo técnico.",
        },
        novoComando: {
          titulo: "Nuevo entrenador",
          corpo: "{treinador} asume en el {clube}. Lo que pide la dirigencia: {objetivo}.",
        },
        bemVindoAoClube: {
          titulo: "Bienvenido al club",
          corpo: "{treinador} asume la conducción del {clube}. El objetivo de la temporada: {objetivo}.",
        },
        pressao: {
          titulo: "Presión de la dirigencia",
          corpo:
            "El objetivo: {objetivo}. El equipo va {posicaoNaTabela}º y la paciencia se está acabando.",
        },
        demissao: {
          titulo: "Fuiste despedido",
          corpo:
            "La dirigencia pedía esto: {objetivo}. El equipo terminó {posicao}º y tu contrato no fue renovado.",
        },
      },

      mercado: {
        propostaDaMaquina: {
          titulo: "Oferta de {clube}",
          corpo: "{clube} ofreció {valor} por {jogador}. Responde en el Mercado.",
        },
        contratacaoLivre: {
          titulo: "Fichaje cerrado",
          corpo: "{jogador} ({posicao}, nivel {forca}) llega al {clube} sin costo.",
        },
        contratacaoAVista: {
          titulo: "Fichaje cerrado",
          corpo: "{jogador} ({posicao}, nivel {forca}) llega al {clube} por {valor}.",
        },
        contratacaoParcelada: {
          titulo: "Fichaje cerrado",
          corpo:
            "{jogador} ({posicao}, nivel {forca}) llega al {clube} por {valor} en {parcelas} cuotas.",
        },
        vendaLivre: {
          titulo: "Jugador vendido",
          corpo: "{jogador} dejó el {vendedor} rumbo al {comprador} sin costo.",
        },
        vendaAVista: {
          titulo: "Jugador vendido",
          corpo: "{jogador} dejó el {vendedor} rumbo al {comprador} por {valor}.",
        },
        vendaParcelada: {
          titulo: "Jugador vendido",
          corpo:
            "{jogador} dejó el {vendedor} rumbo al {comprador} por {valor} en {parcelas} cuotas.",
        },
        emprestimoFechado: {
          titulo: "Cesión cerrada",
          corpo:
            "{jogador} ({posicao}, nivel {forca}) juega en el {clube} hasta fin de temporada, y el club se hace cargo del {parte}% del sueldo.",
        },
        jogadorEmprestado: {
          titulo: "Jugador cedido",
          corpo:
            "{jogador} ({posicao}, nivel {forca}) fue cedido al {clube} hasta fin de temporada, que asume el {parte}% del sueldo.",
        },
        fimDeEmprestimo: {
          titulo: "Fin de la cesión",
          corpo: "{jogador} vuelve al {dono} tras la temporada en el {emprestador}.",
        },
        relatorioDeOlheiro: {
          titulo: "Informe del ojeador",
          corpo: "{jogador} fue observado. Techo estimado entre {minimo} y {maximo}. {veredito}",
        },
        propostaExpirada: {
          titulo: "Oferta vencida",
          corpo: "La oferta por {jogador} venció sin respuesta.",
        },
        disputaVencida: {
          titulo: "Puja ganada",
          corpo: "{clubes} clubes peleaban por {jogador}. Se lo llevó la mejor oferta.",
        },
        janelaFechada: {
          titulo: "Ventana cerrada",
          corpo: "El mercado está cerrado en esta jornada.",
        },
        caixaInsuficiente: {
          titulo: "Caja insuficiente",
          corpo: "La primera cuota de {entrada} supera la caja disponible.",
        },
        emprestimoRecusado: {
          titulo: "Cesión rechazada",
          corpo: "La cesión no salió: {motivo}",
        },
        olheiroParado: {
          titulo: "Ojeador parado",
          corpo: "El ojeador no salió: {motivo}",
        },
        contratoEncerrado: {
          titulo: "Contrato terminado",
          corpo: "{jogador} dejó el {clube} al terminar su contrato y quedó libre.",
        },
      },

      competicao: {
        gruposEncerrados: {
          titulo: "Terminó la fase de grupos de la {competicao}",
          corpo: "{clubes} clubes siguen con vida en la pelea por el título continental.",
        },
        campeaoDaCopa: {
          titulo: "{clube} es campeón",
          corpo: "{clube} se quedó con la {competicao}.",
        },
        temporadaComeca: {
          titulo: "Arranca la temporada {temporada}",
          corpo: "Son {rodadas} fechas hasta conocer a los campeones.",
        },
        campeaoDaLiga: {
          titulo: "{clube} se queda con la {competicao}",
          corpo:
            "Con {pontos} puntos en {jogos} partidos, el {clube} es campeón de la temporada {temporada}.",
        },
        acesso: {
          titulo: "Ascenso conseguido",
          corpo: "El {clube} sube a la {divisao}.",
        },
        rebaixamento: {
          titulo: "Descenso",
          corpo: "El {clube} baja a la {divisao}.",
        },
        nacoesJogo: {
          titulo: "Copa de Naciones: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "En {fase}, el que avanza es {vencedor}.",
        },
        nacoesJogoNosPenaltis: {
          titulo: "Copa de Naciones: {mandante} {golsMandante} x {golsVisitante} {visitante}",
          corpo: "En {fase}, el lugar se definió por penales. El que avanza es {vencedor}.",
        },
        nacoesCampea: {
          titulo: "{selecao} gana la Copa de Naciones",
          corpo: "El título queda para la selección. El torneo vuelve en {temporadas} temporadas.",
        },
      },
    },

    rotulos: {
      rivalidade: {
        historica: "Rivalidad histórica",
        regional: "Clásico regional",
        recente: "Rivalidad reciente",
      },
      olheiro: {
        vaiCrescer: "Tiene mucho por crecer y vale la apuesta.",
        quaseFeito: "Todavía evoluciona, pero ya es casi lo que va a ser.",
        noTeto: "Llegó a su techo. Lo que ves es lo que hay.",
      },
      reputacao: {
        "1": "Municipal",
        "2": "Estatal",
        "3": "Regional",
        "4": "Nacional",
        "5": "Continental",
        "6": "Mundial",
      },
      objetivo: {
        titulo: "Ser campeón",
        brigarPeloTitulo: "Pelear por el título",
        topQuatro: "Terminar entre los cuatro primeros",
        acesso: "Conseguir el ascenso",
        meioDeTabela: "Terminar a mitad de tabla",
        fugirDoRebaixamento: "Evitar el descenso",
      },
      nacoes: {
        final: "la final",
        semifinal: "la semifinal",
        quartas: "los cuartos de final",
        oitavas: "los octavos de final",
        faseInicial: "la ronda inicial",
      },
      // A fase de um mata-mata de clubes. A chave é o identificador que o save
      // guarda em `Fixture.stage`; o nome escrito vive aqui.
      fase: {
        preliminar: "Ronda preliminar",
        inicial: "Ronda inicial",
        primeira: "Primera ronda",
        segunda: "Segunda ronda",
        oitavas: "Octavos de final",
        quartas: "Cuartos de final",
        semifinal: "Semifinales",
        final: "Final",
        grupo: "Grupo {letra}",
        deClubes: "Ronda de {clubes}",
      },
      estrutura: {
        stadium: "Estadio",
        youth: "Divisiones inferiores",
        training: "Centro de entrenamiento",
        medical: "Cuerpo médico",
      },
    },

    // La narración del partido, jugada por jugada. Es el texto que más ve quien
    // juega: pasa en vivo y vuelve entero en el informe.
    lances: {
      golArmacao: "{jogador} define la jugada elaborada.",
      golArmacaoAssistido: "{jogador} define la jugada elaborada, con asistencia de {assistente}.",
      golDrible: "{jogador} se saca la marca de encima y define.",
      golDribleAssistido: "{jogador} se saca la marca de encima y define, tras el pase de {assistente}.",
      golCruzamento: "{jogador} aparece en el área y cabecea.",
      golCruzamentoAssistido: "{jogador} cabecea el centro de {assistente}.",
      golContraAtaque: "{jogador} define en el contragolpe.",
      golContraAtaqueAssistido: "{jogador} define en el contragolpe, tras el pase de {assistente}.",
      golBolaParada: "{jogador} marca de pelota parada.",
      golPenalti: "{jogador} convierte el penal.",
      penaltiPerdido: "{jogador} desperdicia el penal.",
      defesa: "{goleiro} ataja el remate de {jogador}.",
      paraFora: "{jogador} remata y se va afuera.",
      vermelhoDireto: "{jogador} se va expulsado directo.",
      segundoAmarelo: "{jogador} ve la segunda amarilla y queda expulsado.",
      amarelo: "{jogador} ve la tarjeta amarilla.",
      lesao: "{jogador} siente una lesión y necesita atención.",
      substituicao: "{entra} entra por {sai}.",
      decisaoPorPenaltis:
        "Definición por penales: {mandante} {golsMandante} x {golsVisitante} {visitante}.",
    },

    // Por qué el motor rechazó una orden. Sale por dos caminos: directo a la
    // pantalla cuando el clic fue recién, y como `{motivo}` de una noticia
    // cuando la orden vino de la cola de decisiones.
    motivos: {
      escalacao: {
        faltamTitulares: "La alineación necesita 11 titulares.",
        jogadorRepetido: "Un jugador aparece dos veces en la alineación.",
        jogadorDesconhecido: "Hay un jugador desconocido en la alineación.",
        deOutroClube: "Hay un jugador alineado que no pertenece al club.",
        lesionado: "{jogador} arrastra una lesión.",
        suspenso: "{jogador} está suspendido.",
        naSelecao: "{jogador} está con la selección y no puede jugar por ahora.",
        semGoleiro: "Alineá al menos un arquero.",
        titularNoBanco: "Un titular también figura en el banco.",
        reservaInvalido: "Hay un suplente que no pertenece al club.",
        bancoCheio: "El banco acepta {maximo} jugadores como máximo.",
      },
      emprestimo: {
        jogadorDesconhecido: "Ese jugador no existe.",
        jogadorLivre: "A un jugador libre se lo ficha, no se lo pide a préstamo.",
        jaEmprestado: "Ya está a préstamo.",
        jaEDoClube: "Ya es de tu club.",
        clubeDesconhecido: "Ese club no existe.",
        elencoDoDonoCurto: "El {clube} quedaría con el plantel demasiado corto.",
        elencoCheio: "Tu plantel ya está completo.",
        contratoAcabando: "Su contrato termina esta temporada.",
        salarioInsuficiente: "El {clube} quiere que te hagas cargo de al menos {parte}% del sueldo.",
      },
      base: {
        garotoSumiu: "Ese chico ya no está en la cantera.",
        elencoCheio: "La plantilla ya está en el límite de {maximo} jugadores.",
        idadeNoLimite: "{jogador} no puede pasar de los {idade} años en la cantera.",
        semEssaProposta: "Esa oferta ya no está sobre la mesa.",
        compradorSemVaga: "{clube} se quedó sin plaza en la plantilla.",
        janelaFechada: "El mercado de fichajes está cerrado.",
        tecnicoIndisponivel: "Ese técnico ya no está disponible.",
        caixaNaoCobreMulta: "La caja no cubre la indemnización de {multa}.",
      },
      observacao: {
        jogadorDesconhecido: "Ese jugador no existe.",
        doProprioElenco: "No necesitás un veedor para tu propio plantel.",
        jaEntregue: "Tu veedor ya entregó ese informe esta temporada.",
        semCaixa: "Observar a ese jugador cuesta {custo}.",
      },
      banco: {
        valorInvalido: "Monto inválido.",
        limiteDoBanco: "El banco presta {limite} como máximo.",
      },
      emprego: {
        treinadorDesconhecido: "Entrenador no encontrado.",
        soHumanoTroca: "Solo un entrenador humano cambia de club por acá.",
        foraDaJanela: "El cambio de club solo ocurre entre temporadas.",
        clubeDesconhecido: "Club no encontrado.",
        semProposta: "Ese club no te hizo una propuesta.",
        clubeOcupado: "Ese club ya tiene entrenador.",
      },
    },

    // Los logros del equipo B, en la ficha del chico.
    feitos: {
      artilheiro: "Máximo goleador de la Liga de Reservas {temporada} · {gols} goles en {jogos} partidos",
      campeao: "Campeón de la Liga de Reservas {temporada}",
      melhorEmCampo: "Mejor del partido · {vezes}× en {temporada}",
      hatTrick: "Triplete contra {adversario} B · {temporada}",
      gols: "{gols} goles en {jogos} partidos · {temporada}",
      assistencias: "{assistencias} asistencias · {temporada}",
      semSofrerGol: "{jogos} partidos sin encajar · {temporada}",
      selecaoDaRodada: "Once de la jornada · {vezes}× en {temporada}",
    },

    // Las observaciones sueltas del informe del veedor, una por línea.
    laudo: {
      lider: "Lidera el vestuario.",
      profissional: "Profesional ejemplar en el día a día.",
      ambicioso: "Ambicioso: quiere jugar y quiere crecer.",
      temperamental: "Temperamental, y eso se nota en la cancha.",
      mercenario: "Mercenario: va detrás del mejor sueldo.",
      tranquilo: "Tranquilo, no le da trabajo al grupo.",
      lesoesPreocupam: "Su historial de lesiones preocupa.",
      fisicoNaMedia: "Físicamente dentro del promedio.",
      raramenteSeMachuca: "Rara vez se lesiona.",
      idadeAFavor: "La edad juega a favor: hay tiempo para formarlo.",
      contratoPertoDoFim: "Contrato cerca del final. Se puede negociar mejor.",
    },

    // Nome e descrição de cada conquista de carreira. O motor manda a chave; o
    // identificador dela, que é o que o save guarda, nunca aparece na tela.
    conquistas: {
      primeiroTitulo: { nome: "Primer título", descricao: "Levanta la primera copa de tu carrera." },
      campeaoNacional: { nome: "Campeón nacional", descricao: "Gana la primera división de un país." },
      donoDoEstado: { nome: "Dueño del estado", descricao: "Gana un campeonato estatal." },
      campeaoDaCopa: { nome: "La copa es otra cosa", descricao: "Gana una copa nacional." },
      campeaoContinental: { nome: "Al otro lado del continente", descricao: "Gana la competición continental." },
      invicto: { nome: "Nadie pudo", descricao: "Termina una liga sin perder ni un partido." },
      contasEmDia: { nome: "Cuentas al día", descricao: "Cierra la temporada con ganancia y sin deber nada al banco." },
      goleador: { nome: "Goleador de respeto", descricao: "Ten un jugador con 25 goles o más en una temporada." },
      superouAMeta: { nome: "Por encima de lo pactado", descricao: "Termina muy por encima de lo que pedía la directiva." },
      tricampeao: { nome: "Tricampeón", descricao: "Gana la misma competición tres veces." },
      dinastia: { nome: "Dinastía", descricao: "Suma diez títulos en tu carrera." },
      casaEComida: { nome: "Casa y comida", descricao: "Completa cinco temporadas seguidas en el mismo club." },
      andarilho: { nome: "Pasaporte sellado", descricao: "Dirige clubes de tres países diferentes." },
      doFundoDoPoco: { nome: "Desde el fondo", descricao: "Consigue el ascenso dirigiendo a un club fuera de la élite." },
    },

    // O que há de errado num banco de dados, para quem o editou.
    datapack: {
      naoEDatapack: "El archivo no es un datapack.",
      formatoDiferente: "Este juego lee datapacks en formato {esperado}, y el archivo dice {encontrado}.",
      semPais: "El datapack necesita al menos un país.",
      siglaDoPais: "El país necesita una sigla de hasta {maximo} letras.",
      siglaRepetida: "La sigla {sigla} aparece dos veces.",
      semNomeDoPais: "Falta el nombre del país.",
      semDivisao: "El país necesita al menos una división.",
      divisaoCurta: "Una división necesita al menos {minimo} clubes para tener calendario.",
      divisaoImpar: "Una división con número impar de clubes deja a alguien sin partido en cada jornada.",
      semNomeDoClube: "Falta el nombre del club.",
      elencoCurto: "El {clube} necesita al menos {minimo} jugadores para armar un equipo.",
      semGoleiro: "El {clube} no tiene portero.",
    },

    save: {
      semEsquema: "Este archivo no es una partida guardada de Legacy Football Manager.",
      deVersaoMaisNova:
        "Esta partida se guardó con una versión más nueva del juego (esquema {esquema}). Actualiza la aplicación para abrir la carrera.",
      semMigracao:
        "Esta partida está en el esquema {esquema} y el juego no sabe actualizarla. Puede haber sido editada a mano.",
    },

    cartaoDeContratacao: {
      semCusto: "Sin costo",
      aVista: "A cambio de {valor}",
      parcelado: "A cambio de {valor} en {parcelas} cuotas",
    },

    fatores: {
      posse: "El mediocampo del {clube} dominó el partido, con {posse}% de posesión.",
      eficiencia:
        "El {melhor} fue más eficaz: {golsDoMelhor} gol(es) en {chancesDoMelhor} chances, contra {golsDoPior} en {chancesDoPior} del {pior}.",
      decisivo: "{jogador} lo decidió con {gols} goles.",
      expulsao: "La expulsión a los {minuto} minutos dejó al {clube} con uno menos.",
      setor: "El ataque del {forte} era claramente superior a la defensa del {fraco}.",
      cansaco: "{jogadores} jugadores salieron a la cancha por debajo del 55% de estado físico.",
      truncado: "Partido trabado, con poquísimas chances creadas de los dos lados.",
      semGol: "Mucha chance y ningún gol: {chances} oportunidades desperdiciadas en total.",
      equilibrado: "Partido parejo de principio a fin, sin que ningún equipo lograra imponerse.",
      detalhes: "Partido cerrado, decidido por detalles a favor del {clube}.",
    },
  },

  // -------------------------------------------------------------------------
  // Errores de la API, elegidos por el código estable y no por el mensaje
  // -------------------------------------------------------------------------
  errosDaApi: {
    VALIDATION: "Hay algún campo fuera de lo esperado. Revisá lo que completaste.",
    UNAUTHENTICATED: "Tu sesión terminó. Entrá de nuevo para seguir.",
    FORBIDDEN: "Tu cuenta no tiene permiso para eso.",
    NOT_FOUND: "No encontré lo que pediste.",
    EMAIL_TAKEN: "Ya existe una cuenta con ese correo.",
    ALPHA_KEY_INVALID: "Esa clave de acceso no existe o ya fue usada.",
    DEVICE_LIMIT: "Esta cuenta ya activó demasiados dispositivos en los últimos 30 días.",
    INVALID_CREDENTIALS: "El correo y la contraseña no coinciden.",
    EMAIL_NOT_VERIFIED: "Confirmá tu correo antes de entrar.",
    TOKEN_EXPIRED: "Tu sesión venció. Entrá de nuevo.",
    ENTITLEMENT_REQUIRED: "Eso es parte de algo que tu cuenta todavía no tiene.",
    LEAGUE_FULL: "Esa liga ya está llena.",
    LEAGUE_STARTED: "Esa liga ya empezó y no acepta más gente.",
    DEADLINE_PASSED: "Se venció el plazo de la jornada. Tus órdenes valen para la siguiente.",
    ROUND_PROCESSING: "La jornada se está procesando. Probá de nuevo en un momento.",
    NOT_MEMBER: "No estás en esa liga.",
    NOT_COMMISSIONER: "Solo el comisionado de la liga puede hacer eso.",
    INVALID_LINEUP: "Esa alineación no es válida.",
    WINDOW_CLOSED: "El mercado de pases está cerrado.",
    INSUFFICIENT_FUNDS: "La caja del club no cubre ese monto.",
    OFFER_BELOW_MINIMUM: "La oferta está por debajo del mínimo que acepta el dueño.",
    TRADE_COOLDOWN: "Esperá un poco antes de volver a negociar con ese club.",
    PLAYER_UNAVAILABLE: "Ese jugador no está disponible ahora.",
    RATE_LIMITED: "Hiciste demasiados pedidos en poco tiempo. Esperá un instante.",
    ENGINE_VERSION_MISMATCH: "Esta aplicación está desactualizada. Actualizala para seguir.",
    CONFLICT: "Otra persona tocó esto antes que vos. Recargá y probá de nuevo.",
    PAYLOAD_TOO_LARGE: "El archivo supera el tamaño aceptado.",
    BILLING_NOT_CONFIGURED: "La tienda está fuera de servicio por ahora. Probá más tarde.",
    LINKED_ACCOUNT: "Otra cuenta vinculada a esta ya está en esa liga.",
    NATIONS_WINDOW_CLOSED: "La convocatoria solo abre en el año de la Copa de las Naciones.",
    REDE: "No pude hablar con el servidor. Revisá tu conexión.",
    INTERNAL: "Algo salió mal en el servidor. Probá de nuevo en un momento.",
  },
} satisfies Dicionario;
